/* Sakal Publications storefront behaviour.
 * One module, delegated listeners, no dependencies. Every block is idempotent:
 * components mark themselves with data-sk-ready so Theme Editor re-renders don't double-bind.
 * Cart: Shopify Ajax Cart API only. Checkout stays a native button[name=checkout] that
 * Shiprocket replaces (docs/SAKAL_SHIPROCKET_PRESERVATION.md). */
(() => {
  if (window.Sakal) return;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const url = (p) => root + p.replace(/^\//, '');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  document.documentElement.classList.add('sk-js');

  /* ---------- Toast ---------- */
  let toastEl, toastTimer;
  function toast(msg, type) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'sk-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.toggle('sk-toast--error', type === 'error');
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3600);
  }

  /* ---------- Motion helpers (shared by cart + wishlist feedback) ---------- */
  // Web Animations API only (no library): a copy of the book cover flies in a soft arc to a header icon.
  // The copy is a fixed, pointer-events:none element removed when the flight ends, so it never blocks taps.
  // Callers run it only AFTER the action succeeded. Reduced motion, missing geometry or a hidden target → no flight.
  const Motion = {
    visible(el) {
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;
    },
    target(selector) { return $$(selector).find((el) => this.visible(el)) || null; },
    // Where a flight starts: the card's cover, else the visible gallery image of the product (book page, Quick view)
    cover(el) {
      if (!el) return null;
      const card = el.closest('.sk-card');
      if (card) return $('.sk-card__media img', card);
      const form = el.form || el.closest('form');
      const product = el.closest('[data-sk-product]') || (form && form.closest('[data-sk-product]'));
      return product ? $$('[data-sk-gallery-main] img, .sk-gallery img', product).find((im) => this.visible(im)) || null : null;
    },
    // Capture position + source now (the source may be inside a dialog that is about to close)
    snapshot(img) {
      if (!img || !this.visible(img)) return null;
      return { rect: img.getBoundingClientRect(), src: img.currentSrc || img.src };
    },
    fly(snap, to, { size = 0, duration = 640 } = {}) {
      if (reduceMotion.matches || !snap || !snap.src || !to || !this.visible(to) || typeof Element.prototype.animate !== 'function') return Promise.resolve();
      const a = snap.rect;
      const b = to.getBoundingClientRect();
      const w = size || a.width;
      const h = size ? size * (a.height / a.width) : a.height;
      const x0 = a.left + (a.width - w) / 2;
      const y0 = a.top + (a.height - h) / 2;
      const dx = b.left + b.width / 2 - (x0 + w / 2);
      const dy = b.top + b.height / 2 - (y0 + h / 2);
      const end = Math.max(0.06, Math.min(1, 20 / Math.max(w, h)));
      const outer = document.createElement('div');
      outer.className = 'sk-fly';
      outer.setAttribute('aria-hidden', 'true');
      Object.assign(outer.style, { left: x0 + 'px', top: y0 + 'px', width: w + 'px', height: h + 'px' });
      const inner = document.createElement('div');
      inner.className = 'sk-fly__inner';
      const img = document.createElement('img');
      img.src = snap.src;
      img.alt = '';
      inner.appendChild(img);
      outer.appendChild(inner);
      document.body.appendChild(outer);
      // X and Y run on different curves, which bends the straight path into an arc (rises first, then glides over)
      const x = outer.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${dx}px)` }], { duration, easing: 'cubic-bezier(.5, 0, .3, 1)', fill: 'forwards' });
      const y = inner.animate([
        { transform: 'translateY(0) scale(1)', opacity: 1 },
        { transform: `translateY(${dy * 0.55}px) scale(${(1 + end) / 1.6})`, opacity: 1, offset: 0.5 },
        { transform: `translateY(${dy}px) scale(${end})`, opacity: 0.35 },
      ], { duration, easing: 'cubic-bezier(.2, .65, .3, 1)', fill: 'forwards' });
      return new Promise((resolve) => {
        let done = false;
        const finish = () => { if (done) return; done = true; outer.remove(); resolve(); };
        Promise.all([x.finished, y.finished]).then(finish, finish);
        setTimeout(finish, duration + 300); // never leave the copy behind (e.g. animation cancelled)
      });
    },
    // Restart a one-shot CSS animation class
    bump(el, cls) {
      if (!el || reduceMotion.matches) return;
      el.classList.remove(cls);
      void el.offsetWidth;
      el.classList.add(cls);
      setTimeout(() => el.classList.remove(cls), 700);
    },
    modalOpen() { return $$('dialog[open]').some((d) => d.matches(':modal')); },
  };

  /* ---------- Brand loader (assets/sakal-loader.gif) ---------- */
  // Shown while something loads: leaving for another page or reloading (the current page stays covered until the
  // next one appears — never delaying the new page itself), Add to cart, Buy now, bag updates, shelves, Quick view.
  // One overlay element, one registry of operations (`ops`) as the single source of truth:
  //   beginLoading(id, opts) / endLoading(id)  — tracked operation; ids may be reused ('navigation')
  //   showLoader(context) / hideLoader(context) — manual show/hide, same registry
  //   page(label, delay) / inline(host)         — anonymous operation, returns its end() (used by this file)
  // The overlay is visible while any started full-page operation is past its threshold, so concurrent operations
  // never hide each other. Every operation has its own failsafe (MAX) keyed by a token, so an old timeout can never
  // end a newer run of the same id. Ending is idempotent; the overlay fades out without intercepting taps.
  // The logo appears only if the wait passes a short threshold, so instant operations don't flash it.
  // The GIF is fetched after the page has loaded (idle time) or when an operation starts — never during render.
  // URLs come from body[data-sk-loader] (Liquid asset_url); reduced motion gets a still frame instead of the GIF.
  const Loader = {
    DELAY: 300,
    FADE: 150, // --sk-t-fast
    MAX: Number(document.body && document.body.dataset.skLoaderMax) || 15000, // failsafe per operation
    ops: new Map(), // id → { token, label, passive, host, shown, timer, safety }
    seq: 0,
    hosts: new WeakMap(), // inline host → { mark, n }
    el: null,
    label: null,
    next: null, // pre-decoded disc for the next appearance
    leaveTimer: 0,
    src() {
      const d = document.body.dataset;
      return (reduceMotion.matches ? d.skLoaderStill : d.skLoader) || '';
    },
    // Start the download when a qualifying operation begins (not at page load), so the logo is ready if the wait
    // reaches the threshold; once cached, later operations reuse it. A failed download is retried next time.
    warm() {
      if (this.warmed) return;
      this.warmed = true;
      const u = this.src();
      // Kept in memory so every appearance can start the animation from its first frame (see fresh())
      fetch(u).then((r) => (r.ok ? r.blob() : Promise.reject())).then((blob) => { this.blob = blob; }).catch(() => { this.warmed = false; });
    },
    // Browsers keep one animation clock per GIF URL, so a reused URL can surface mid-loop (in the logo's blank
    // frames). A new object URL of the same downloaded bytes is a new image that starts at frame 1 — no extra
    // request. Falls back to the plain asset URL until the bytes are in memory (or if the fetch was refused).
    fresh() {
      if (!this.blob || reduceMotion.matches) return { src: this.src(), revoke() {} };
      const src = URL.createObjectURL(this.blob);
      return { src, revoke: () => URL.revokeObjectURL(src) };
    },
    mark(cls) {
      const span = document.createElement('span');
      span.className = cls;
      span.setAttribute('aria-hidden', 'true');
      const img = document.createElement('img');
      img.className = 'sakal-loader__logo';
      const f = this.fresh();
      img.src = f.src;
      span.revoke = f.revoke;
      img.alt = '';
      img.width = 160;
      img.height = 160;
      img.decoding = 'async';
      if (img.decode) img.decode().catch(() => {}); // decoded off-screen, before it is first shown
      span.appendChild(img);
      return span;
    },
    beginLoading(id, { label = 'Loading…', delay = this.DELAY, passive = false, host = null } = {}) {
      id = id || 'sk-op-' + ++this.seq;
      if (!this.src()) return id;
      const token = ++this.seq;
      const old = this.ops.get(id);
      if (old) {
        // Already running: a repeat begin refreshes its label and failsafe, it never stacks a second overlay
        old.token = token;
        old.label = label;
        clearTimeout(old.safety);
        old.safety = setTimeout(() => this.expire(id, token), this.MAX);
        if (old.shown && !old.host) this.render();
        return id;
      }
      this.warm();
      const op = { token, label, passive, host, shown: false, timer: 0, safety: 0 };
      this.ops.set(id, op);
      if (!host && !this.visible()) this.prepare();
      op.timer = setTimeout(() => {
        if (this.ops.get(id) !== op) return;
        if (host) op.shown = this.attach(host);
        else { op.shown = true; this.render(); }
      }, Math.max(0, delay));
      op.safety = setTimeout(() => this.expire(id, token), this.MAX);
      return id;
    },
    endLoading(id) {
      const op = this.ops.get(id);
      if (!op) return;
      this.ops.delete(id);
      clearTimeout(op.timer);
      clearTimeout(op.safety);
      if (!op.shown) return;
      if (op.host) this.detach(op.host);
      else this.render();
    },
    // Failsafe: ends the operation only if it is still the same run that armed this timer
    expire(id, token) {
      const op = this.ops.get(id);
      if (op && op.token === token) this.endLoading(id);
    },
    showLoader(context = 'manual', label) { return this.beginLoading(context, { label, delay: 0 }); },
    hideLoader(context = 'manual') { this.endLoading(context); },
    page(label, delay = this.DELAY) {
      const id = this.beginLoading(null, { label, delay });
      return () => this.endLoading(id);
    },
    // Inside a busy region (cart lines, Quick view body, a shelf): the logo sits over the region's veil/skeleton
    inline(host) {
      if (!host) return () => {};
      const id = this.beginLoading(null, { host });
      return () => this.endLoading(id);
    },
    attach(host) {
      if (!host.isConnected) return false;
      let h = this.hosts.get(host);
      if (!h) {
        h = { mark: this.mark('sakal-loader-inline'), n: 0 };
        if (getComputedStyle(host).position === 'static') host.classList.add('sakal-loader-host');
        host.appendChild(h.mark);
        this.hosts.set(host, h);
      }
      h.n++;
      return true;
    },
    detach(host) {
      const h = this.hosts.get(host);
      if (!h || --h.n > 0) return;
      this.hosts.delete(host);
      h.mark.remove();
      if (h.mark.revoke) h.mark.revoke();
      host.classList.remove('sakal-loader-host');
    },
    visible() { return !!this.el && this.el.classList.contains('is-on'); },
    ensure() {
      if (this.el) return;
      const el = document.createElement('div');
      el.className = 'sakal-loader';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      if ('popover' in el) el.popover = 'manual'; // top layer: above an open Quick view / drawer
      const text = document.createElement('p');
      text.className = 'sakal-loader__label';
      el.append(text);
      this.el = el;
      this.label = text;
      // If the modal it lives in closes mid-wait (Buy now closes Quick view), move it back out
      document.addEventListener('toggle', (e) => {
        if (e.newState === 'closed' && this.visible() && e.target instanceof HTMLDialogElement && e.target.contains(el)) this.place();
      }, true);
    },
    prepare() { if (!this.next) this.next = this.mark('sakal-loader__disc'); },
    // Restart the logo animation for a new appearance (only when fully hidden, never while it is on screen)
    swapDisc() {
      const next = this.next || this.mark('sakal-loader__disc');
      this.next = null;
      const cur = this.el.querySelector('.sakal-loader__disc');
      if (cur) { if (cur.revoke) cur.revoke(); cur.replaceWith(next); } else this.el.prepend(next);
    },
    // Overlay state from the registry: visible while any full-page operation is shown; the latest one names it.
    // Passive (navigation) overlays never intercept taps, so a navigation that doesn't leave can't block the page.
    render() {
      let top = null;
      let passive = true;
      for (const op of this.ops.values()) if (op.shown && !op.host) { top = op; if (!op.passive) passive = false; }
      if (!top) return this.hide();
      this.ensure();
      const el = this.el;
      this.label.textContent = top.label || 'Loading…';
      el.classList.toggle('is-passive', passive);
      if (el.classList.contains('is-on')) return;
      clearTimeout(this.leaveTimer);
      if (!el.classList.contains('is-leaving')) this.swapDisc();
      el.classList.remove('is-leaving');
      this.place();
      el.classList.add('is-on');
    },
    hide() {
      const el = this.el;
      if (!el || !el.classList.contains('is-on')) return;
      el.classList.remove('is-on');
      el.classList.add('is-leaving');
      clearTimeout(this.leaveTimer);
      this.leaveTimer = setTimeout(() => {
        el.classList.remove('is-leaving');
        if (el.popover) { try { el.hidePopover(); } catch (_) {} }
      }, reduceMotion.matches ? 0 : this.FADE);
    },
    // Everything outside an open modal dialog is inert, so the overlay lives inside the topmost one (else <body>)
    place() {
      const el = this.el;
      const host = $$('dialog[open]').filter((d) => d.matches(':modal')).pop() || document.body;
      if (el.parentNode !== host) {
        if (el.popover && el.matches(':popover-open')) { try { el.hidePopover(); } catch (_) {} }
        host.appendChild(el);
      }
      if (el.popover && !el.matches(':popover-open')) { try { el.showPopover(); } catch (_) {} }
    },
    reset() {
      for (const id of [...this.ops.keys()]) this.endLoading(id);
      clearTimeout(this.leaveTimer);
      if (!this.el) return;
      this.el.classList.remove('is-on', 'is-leaving');
      if (this.el.popover) { try { this.el.hidePopover(); } catch (_) {} }
    },
  };
  // A request this theme makes that never answers rejects after Loader.MAX like a failed one (TimeoutError), so
  // every caller's catch/finally releases its button, veil and loader. A caller's own signal still cancels it.
  function fetchT(input, init = {}) {
    const ctrl = new AbortController();
    const outer = init.signal;
    const relay = () => ctrl.abort(outer.reason);
    if (outer) { if (outer.aborted) relay(); else outer.addEventListener('abort', relay, { once: true }); }
    const timer = setTimeout(() => ctrl.abort(new DOMException('Request timed out', 'TimeoutError')), Loader.MAX);
    return fetch(input, { ...init, signal: ctrl.signal }).finally(() => {
      clearTimeout(timer);
      if (outer) outer.removeEventListener('abort', relay);
    });
  }
  // Have the logo cached before the first navigation needs it: fetched in idle time after the page has loaded
  window.addEventListener('load', () => {
    const go = () => Loader.warm();
    if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 4000 }); else setTimeout(go, 1500);
  }, { once: true });
  // Page loads and reloads: links, menus, search, filters, sort, pagination, refresh. The Navigation API reports
  // every page navigation the page starts (links, forms, scripted, location.reload()) and none of the in-page ones:
  // drawer and dialog toggles cancel their clicks, so they never navigate; new-tab clicks, downloads, hash jumps and
  // mailto/tel/WhatsApp links don't load a new page here. Browsers without it use link clicks + form submits.
  // The browser's own reload button / pull-to-refresh / typed URL are only visible as `beforeunload`.
  // A navigation that turns out not to leave (204, attachment, cancelled prompt) gives no signal, so this overlay is
  // passive (taps go through), Escape dismisses it, and its failsafe ends it; the next navigation shows it again.
  const navStart = () => Loader.beginLoading('navigation', { label: 'Loading…', delay: 150, passive: true });
  // Back/forward cache restores a page exactly as it was left (e.g. mid hand-off to checkout): clear every operation
  window.addEventListener('pageshow', (e) => { if (e.persisted) Loader.reset(); else Loader.endLoading('navigation'); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') Loader.endLoading('navigation'); });
  if ('navigation' in window && window.navigation && typeof window.navigation.addEventListener === 'function') {
    navigation.addEventListener('navigate', (e) => {
      if (e.destination.sameDocument || e.hashChange || e.downloadRequest !== null) return;
      // Leaving the site (another origin) is the other site's loading, not ours
      if (new URL(e.destination.url).origin !== location.origin) return;
      navStart();
    });
  } else {
    // Late listeners on window: anything the theme handles itself has already called preventDefault()
    window.addEventListener('click', (e) => {
      const a = e.target instanceof Element && e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if ((a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      const u = new URL(a.href, location.href);
      if (u.origin !== location.origin || !/^https?:$/.test(u.protocol)) return;
      if (u.pathname === location.pathname && u.search === location.search && u.hash) return; // in-page jump
      navStart();
    });
    window.addEventListener('submit', (e) => {
      const f = e.target;
      if (e.defaultPrevented || !(f instanceof HTMLFormElement) || (f.target && f.target !== '_self')) return;
      navStart();
    });
  }
  // Phone / email / app links, downloads and new tabs also fire beforeunload but never leave this page: a tap on
  // one of those in the last second means this beforeunload is not a page load
  let stayClick = 0;
  document.addEventListener('click', (e) => {
    const a = e.target instanceof Element && e.target.closest('a[href]');
    if (!a) return;
    const u = new URL(a.href, location.href);
    if (!/^https?:$/.test(u.protocol) || a.hasAttribute('download') || (a.target && a.target !== '_self')) stayClick = Date.now();
  }, true);
  window.addEventListener('beforeunload', () => { if (Date.now() - stayClick > 1000) navStart(); }); // reload button, pull-to-refresh, typed URL

  /* ---------- Wishlist ---------- */
  // Storage adapter: the ONLY code that knows where the list lives (this browser's localStorage, falling back to
  // memory when storage is blocked). A customer-account or wishlist-app adapter can replace it with the same
  // read / write / subscribe contract without touching the UI below. Stored per entry: product id + handle + time.
  // Not synced across devices or accounts.
  const WishStore = (() => {
    const KEY = 'sk-wishlist-v1';
    let memory = [];
    let persistent = true;
    try { localStorage.setItem('sk-wl-probe', '1'); localStorage.removeItem('sk-wl-probe'); } catch (_) { persistent = false; }
    return {
      get persistent() { return persistent; },
      read() {
        if (!persistent) return memory;
        try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (_) { return []; }
      },
      write(list) {
        memory = list;
        if (!persistent) return;
        try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (_) { persistent = false; }
      },
      // Other tabs: the browser fires `storage` there when this key changes
      subscribe(fn) { window.addEventListener('storage', (e) => { if (e.key === KEY || e.key === null) fn(); }); },
    };
  })();
  const WISH_MAX = 200;
  // Keep only well-formed, unique entries (saved data can be old, edited or corrupt)
  function cleanWishlist(raw) {
    const seen = new Set();
    const out = [];
    for (const e of Array.isArray(raw) ? raw : []) {
      if (!e || typeof e !== 'object') continue;
      const id = Number(e.id);
      const handle = typeof e.handle === 'string' ? e.handle.trim() : '';
      if (!Number.isSafeInteger(id) || id <= 0 || seen.has(id) || !handle || handle.length > 255 || /[\s/?#]/.test(handle)) continue;
      seen.add(id);
      out.push({ id, handle, addedAt: Number(e.addedAt) || 0 });
    }
    return out.slice(-WISH_MAX);
  }
  const Wishlist = {
    items: cleanWishlist(WishStore.read()),
    has(id) { const n = Number(id); return this.items.some((e) => e.id === n); },
    // Most recently saved first
    list() { return [...this.items].sort((a, b) => b.addedAt - a.addedAt); },
    commit(next, detail) {
      this.items = next;
      WishStore.write(next);
      this.sync();
      document.dispatchEvent(new CustomEvent('sakal:wishlist:change', { detail }));
    },
    add(id, handle) {
      const n = Number(id);
      if (this.has(n) || !cleanWishlist([{ id: n, handle }]).length) return false;
      this.commit([...this.items, { id: n, handle, addedAt: Date.now() }].slice(-WISH_MAX), { type: 'add', id: n });
      return true;
    },
    remove(id) {
      const n = Number(id);
      if (!this.has(n)) return false;
      this.commit(this.items.filter((e) => e.id !== n), { type: 'remove', id: n });
      return true;
    },
    // A book's handle changed (Shopify redirected the old URL): keep the entry pointing at the live URL
    rehandle(id, handle) {
      const e = this.items.find((x) => x.id === Number(id));
      if (!e || e.handle === handle || !cleanWishlist([{ id: e.id, handle }]).length) return;
      e.handle = handle;
      WishStore.write(this.items);
    },
    paint(b) {
      const on = this.has(b.dataset.skWish);
      const t = b.dataset.title || 'this book';
      b.dataset.state = on ? 'on' : 'off';
      b.setAttribute('aria-label', on ? `Remove ${t} from wishlist` : `Add ${t} to wishlist`);
      const text = b.querySelector('[data-sk-wish-text]');
      if (text) text.textContent = on ? 'Saved to wishlist' : 'Save to wishlist';
    },
    sync(scope = document) {
      $$('[data-sk-wish]', scope).forEach((b) => this.paint(b));
      const n = this.items.length;
      $$('[data-sk-wish-count]').forEach((el) => { el.textContent = n; el.dataset.count = n; });
      $$('[data-sk-wish-link]').forEach((a) => a.setAttribute('aria-label', n ? `Wishlist, ${n} ${n === 1 ? 'book' : 'books'}` : 'Wishlist'));
    },
  };
  WishStore.subscribe(() => {
    Wishlist.items = cleanWishlist(WishStore.read());
    Wishlist.sync();
    document.dispatchEvent(new CustomEvent('sakal:wishlist:change', { detail: { type: 'external' } }));
  });
  let wishStorageWarned = false;
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-sk-wish]');
    if (!b) return;
    e.preventDefault();
    const id = Number(b.dataset.skWish);
    const wasOn = Wishlist.has(id);
    // Capture the flight source before the list changes (the wishlist page removes the card on "remove")
    const snap = wasOn ? null : Motion.snapshot(Motion.cover(b));
    const changed = wasOn ? Wishlist.remove(id) : Wishlist.add(id, b.dataset.handle);
    if (!changed) return;
    if (!WishStore.persistent && !wishStorageWarned) {
      wishStorageWarned = true;
      toast('Saved for this visit only — this browser is blocking site storage.');
    } else {
      toast(wasOn ? 'Removed from wishlist' : 'Added to wishlist');
    }
    if (wasOn) return; // removing just eases back to the outline heart (CSS)
    Motion.bump(b, 'is-pop');
    const target = Motion.modalOpen() ? null : Motion.target('[data-sk-wish-link]');
    Motion.fly(snap, target, { size: 46, duration: 540 }).then(() => Motion.bump(target, 'is-bump'));
  });
  document.addEventListener('sakal:content:changed', () => Wishlist.sync());

  /* ---------- Dialog helpers (drawers) ---------- */
  let lastTrigger = null;
  const settled = new WeakSet(); // drawers whose close has been handled (reset on open)
  function openDialog(d, trigger) {
    if (!d || d.open) return;
    lastTrigger = trigger || document.activeElement;
    settled.delete(d);
    d.showModal();
    document.body.classList.add('sk-lock');
  }
  function closeDialog(d) {
    if (!d || !d.open) return;
    d.close();
  }
  // Unlock scrolling + return focus once per close. `toggle` (newState "closed") arrives promptly; `close` can lag
  // behind it by a second in some Chromium builds, so whichever comes first does the work.
  const onDrawerClosed = (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDialogElement) || !d.classList.contains('sk-drawer') || d.open || settled.has(d)) return;
    if (e.type === 'toggle' && e.newState !== 'closed') return;
    settled.add(d);
    if (!$$('dialog.sk-drawer[open]').length) document.body.classList.remove('sk-lock');
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
  };
  document.addEventListener('toggle', onDrawerClosed, true);
  document.addEventListener('close', onDrawerClosed, true);
  // click on backdrop closes
  document.addEventListener('click', (e) => {
    const d = e.target;
    if (d instanceof HTMLDialogElement && d.classList.contains('sk-drawer') && d.open) {
      const r = d.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) closeDialog(d);
    }
  });
  document.addEventListener('click', (e) => {
    const c = e.target.closest('[data-sk-close]');
    if (c) closeDialog(c.closest('dialog'));
    const o = e.target.closest('[data-sk-open]');
    if (o) {
      const d = document.getElementById(o.getAttribute('data-sk-open'));
      if (d) { e.preventDefault(); openDialog(d, o); }
    }
  });

  /* ---------- Free delivery: animated progress + celebration burst ---------- */
  // body[data-free-ship] (cents) exists only when the free-delivery setting is on. After each cart update the
  // re-rendered progress blocks (snippets/sakal-free-shipping.liquid, [data-sk-ship]) animate from the previous
  // total; the moment the total first reaches the amount, confetti bursts from the block's badge (or the header
  // bag). The burst is decorative only (aria-hidden, no popup); the block's own role="status" text announces it.
  const FreeShip = {
    threshold: Number(document.body.dataset.freeShip) || 0,
    celebrate: document.body.hasAttribute('data-free-ship-celebrate'),
    last: Number(document.body.dataset.cartTotal) || 0,
    pct(total) { return Math.min(100, (total * 100) / this.threshold); },
    check(cart, quiet) {
      if (!this.threshold || !cart || typeof cart.total_price !== 'number') return;
      const prev = this.last;
      this.last = cart.total_price;
      if (!reduceMotion.matches && prev !== cart.total_price) {
        const from = this.pct(prev) + '%';
        const to = this.pct(cart.total_price) + '%';
        $$('[data-sk-ship]').forEach((el) => {
          try { el.animate([{ '--sk-ship-p': from }, { '--sk-ship-p': to }], { duration: 900, easing: 'cubic-bezier(.2, .7, .2, 1)' }); } catch (_) {}
        });
      }
      const crossed = cart.item_count > 0 && prev < this.threshold && cart.total_price >= this.threshold;
      if (!crossed || quiet) return;
      $$('[data-sk-ship]').forEach((el) => {
        el.classList.add('is-unlocking');
        setTimeout(() => el.classList.remove('is-unlocking'), 2600);
      });
      if (this.celebrate && !reduceMotion.matches) setTimeout(() => this.burst(), 500); // once the van reaches the flag
    },
    // Canvas confetti with simple physics: two waves fired from the badge in an upward cone, gravity and air
    // drag, paper pieces that flip as they tumble, a soft ring on launch, fade-out at the end of each life.
    burst() {
      const visible = (el) => { const r = el.getBoundingClientRect(); return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight; };
      const origin = $$('[data-sk-ship-badge]').find(visible) || $$('[data-sk-cart-toggle]').find(visible);
      const r = origin ? origin.getBoundingClientRect() : { left: window.innerWidth / 2, top: window.innerHeight / 3, width: 0, height: 0 };
      const ox = r.left + r.width / 2;
      const oy = r.top + r.height / 2;
      const W = window.innerWidth;
      const H = window.innerHeight;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const canvas = document.createElement('canvas');
      canvas.className = 'sk-burst';
      canvas.setAttribute('aria-hidden', 'true');
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      // Inside an open modal (the cart drawer) so it renders above it; otherwise on the page
      const modal = $$('dialog[open]').filter((d) => d.matches(':modal')).pop();
      (modal || document.body).appendChild(canvas);

      const css = getComputedStyle(document.documentElement);
      const tok = (n, f) => (css.getPropertyValue(n).trim() || f);
      const colors = [tok('--sk-orange', '#ff8729'), tok('--sk-orange-600', '#e8701a'), tok('--sk-blue', '#0b5cab'), tok('--sk-success', '#1f7a4d'), '#2fa36b', '#f5c542', '#ffd27a'];
      const rand = (a, b) => a + Math.random() * (b - a);
      const parts = [];
      const fire = (count, spread, power, delay) => {
        for (let i = 0; i < count; i++) {
          const angle = -Math.PI / 2 + rand(-spread, spread);
          const speed = rand(power * 0.45, power);
          const kind = Math.random() < 0.62 ? 'paper' : Math.random() < 0.6 ? 'strip' : 'dot';
          parts.push({
            x: ox, y: oy, delay,
            vx: Math.cos(angle) * speed * rand(0.9, 1.25),
            vy: Math.sin(angle) * speed,
            rot: rand(0, Math.PI * 2), vr: rand(-0.25, 0.25),
            tilt: rand(0, Math.PI * 2), vt: rand(0.05, 0.16),
            wob: rand(0, Math.PI * 2), vw: rand(0.04, 0.1), amp: rand(0.4, 1.4),
            w: kind === 'strip' ? rand(3.5, 5) : rand(9, 13),
            h: kind === 'strip' ? rand(14, 20) : rand(6, 9),
            kind, color: colors[(Math.random() * colors.length) | 0],
            life: rand(1700, 2500), age: 0,
          });
        }
      };
      fire(120, 1.05, 16, 0);     // main burst
      fire(55, 1.35, 11, 140);    // softer second wave, wider
      fire(26, 0.35, 19, 60);     // a few high flyers straight up

      const start = performance.now();
      let last = start;
      const draw = (now) => {
        const k = Math.min(3, (now - last) / 16.67); // frame-rate independent
        last = now;
        const t = now - start;
        ctx.clearRect(0, 0, W, H);
        // launch ring
        if (t < 520) {
          const p = t / 520;
          ctx.beginPath();
          ctx.arc(ox, oy, 14 + p * 70, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(31, 122, 77, ${0.45 * (1 - p)})`;
          ctx.lineWidth = 3 * (1 - p) + 0.5;
          ctx.stroke();
        }
        let alive = 0;
        for (const q of parts) {
          if (t < q.delay) { alive++; continue; }
          q.age += 16.67 * k;
          if (q.age > q.life || q.y > H + 40) continue;
          alive++;
          const drag = Math.pow(0.965, k);
          q.vx *= drag;
          q.vy = q.vy * drag + 0.32 * k;          // gravity
          q.vy = Math.min(q.vy, 5.2);               // terminal velocity: paper floats down
          q.wob += q.vw * k;
          q.x += (q.vx + Math.sin(q.wob) * q.amp) * k;
          q.y += q.vy * k;
          q.rot += q.vr * k;
          q.tilt += q.vt * k;
          const fade = q.age > q.life * 0.7 ? 1 - (q.age - q.life * 0.7) / (q.life * 0.3) : 1;
          ctx.save();
          ctx.globalAlpha = Math.max(0, fade);
          ctx.translate(q.x, q.y);
          ctx.rotate(q.rot);
          ctx.fillStyle = q.color;
          if (q.kind === 'dot') {
            ctx.beginPath();
            ctx.arc(0, 0, q.h * 0.55, 0, Math.PI * 2);
            ctx.fill();
          } else {
            ctx.scale(1, Math.cos(q.tilt)); // paper flip
            ctx.fillRect(-q.w / 2, -q.h / 2, q.w, q.h);
          }
          ctx.restore();
        }
        if (alive && t < 3200) requestAnimationFrame(draw);
        else canvas.remove();
      };
      requestAnimationFrame(draw);
    },
  };

  /* ---------- Cart ---------- */
  const Cart = {
    busy: false,
    drawer: () => document.getElementById('SkCartDrawer'),
    pageSection: () => $('[data-sk-cart-page]'),
    sections() {
      const ids = [];
      if (this.drawer()) ids.push('sakal-cart-drawer');
      const p = this.pageSection();
      if (p && p.dataset.sectionId) ids.push(p.dataset.sectionId);
      return ids;
    },
    async request(path, body) {
      const sections = this.sections();
      if (sections.length) { body.sections = sections; body.sections_url = window.location.pathname; }
      const res = await fetchT(url(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      let data = {};
      try { data = await res.json(); } catch (_) {}
      if (!res.ok || data.status) {
        const err = new Error(data.description || data.message || 'Something went wrong. Please try again.');
        err.status = res.status;
        throw err;
      }
      return data;
    },
    render(sectionsHtml) {
      if (!sectionsHtml) return;
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
      const d = this.drawer();
      if (d && sectionsHtml['sakal-cart-drawer']) {
        const fresh = parse(sectionsHtml['sakal-cart-drawer']).getElementById('SkCartDrawerContent');
        const cur = document.getElementById('SkCartDrawerContent');
        if (fresh && cur) cur.replaceWith(fresh);
      }
      const p = this.pageSection();
      let done = Promise.resolve();
      if (p && sectionsHtml[p.dataset.sectionId]) {
        const doc = parse(sectionsHtml[p.dataset.sectionId]);
        const fresh = doc.querySelector('[data-sk-cart-page-inner]');
        const cur = p.querySelector('[data-sk-cart-page-inner]');
        if (fresh && cur) done = this.swapPage(cur, fresh);
      }
      Wishlist.sync(); // recommendation cards in the fresh markup
      return done;
    },
    // Cart page swap that keeps what the shopper was doing: [data-sk-persist] widgets (delivery check) move across
    // as live nodes, <details> keep their open state, focus returns to the same control (data-sk-fid / id) or the
    // heading, changed quantities and the total get a short cue. Lines and the summary slide into place through a
    // same-document view transition (names only exist while html.sk-vt-cart is set; root stays interactive).
    swapPage(cur, fresh) {
      const active = document.activeElement;
      const hadFocus = active && active !== document.body && cur.contains(active);
      const fid = hadFocus ? (active.closest('[data-sk-fid]')?.dataset.skFid || '') : '';
      const fidSel = fid ? `[data-sk-fid="${CSS.escape(fid)}"]` : active && active.id ? `#${CSS.escape(active.id)}` : '';
      $$('details[id]', cur).forEach((d) => { const f = fresh.querySelector(`#${CSS.escape(d.id)}`); if (f) f.open = d.open; });
      $$('[data-sk-persist]', cur).forEach((live) => {
        const f = fresh.querySelector(`[data-sk-persist="${CSS.escape(live.dataset.skPersist)}"]`);
        if (f) f.replaceWith(live);
      });
      const qty = new Map($$('[data-sk-line]', cur).map((l) => [l.dataset.key, $('[data-sk-qty-input]', l)?.value]));
      const totalWas = $('[data-sk-sum-total]', cur)?.textContent;
      const swap = () => {
        cur.replaceWith(fresh);
        if (!reduceMotion.matches) {
          $$('[data-sk-line]', fresh).forEach((l) => {
            const input = $('[data-sk-qty-input]', l);
            if (input && qty.has(l.dataset.key) && qty.get(l.dataset.key) !== input.value) input.classList.add('is-bumped');
          });
          const t = $('[data-sk-sum-total]', fresh);
          if (t && totalWas && t.textContent !== totalWas) t.classList.add('is-changed');
        }
        if (hadFocus) {
          const back = (active.isConnected && fresh.contains(active) && active) || (fidSel && $(fidSel, fresh)) || $('[data-sk-cart-heading]', fresh);
          if (back) back.focus({ preventScroll: true });
        }
        initRails(fresh);
        CartPage.bind(fresh);
      };
      if (typeof document.startViewTransition !== 'function' || reduceMotion.matches || document.hidden) { swap(); return Promise.resolve(); }
      const html = document.documentElement;
      html.classList.add('sk-vt-cart');
      try {
        const vt = document.startViewTransition(swap);
        vt.ready.catch(() => {}); // a skipped/aborted animation is not an error: the DOM update still runs
        return vt.updateCallbackDone.catch(() => {}).finally(() => vt.finished.catch(() => {}).finally(() => html.classList.remove('sk-vt-cart')));
      } catch (_) {
        html.classList.remove('sk-vt-cart');
        if (cur.isConnected) swap();
        return Promise.resolve();
      }
    },
    // One status owner per surface: the cart page's live region, else the toast
    announce(msg, type) {
      const live = $('[data-sk-cart-status]');
      if (live) { live.textContent = ''; setTimeout(() => { live.textContent = msg; }, 60); }
      else toast(msg, type);
    },
    async fetchCart() {
      try {
        const res = await fetchT(url('cart.js'), { headers: { Accept: 'application/json' } });
        return res.ok ? await res.json() : null;
      } catch (_) { return null; }
    },
    async refreshCount() {
      const c = await this.fetchCart();
      if (c) this.setCount(c.item_count);
      return c || undefined;
    },
    setCount(n) {
      $$('[data-sk-cart-count]').forEach((el) => {
        el.textContent = n;
        el.dataset.count = n;
      });
      $$('[data-sk-cart-label]').forEach((el) => el.setAttribute('aria-label', `Cart, ${n} ${n === 1 ? 'item' : 'items'}`));
    },
    async add(items, btn, opts = {}) {
      // In flight, or just confirmed (the tick is showing): a repeat tap is not a second order of the book
      if (btn && (btn.classList.contains('is-loading') || btn.classList.contains('is-done'))) return false;
      if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
      // Brand loader while Shopify adds the book (Buy now passes silent and shows its own checkout loader).
      // It clears before the cover flies, so the flight and the drawer are never hidden behind it.
      const endLoader = opts.silent ? () => {} : Loader.page('Adding to your bag…', 150);
      try {
        const data = await this.request('cart/add.js', { items });
        this.render(data.sections);
        const cartNow = await this.fetchCart();
        endLoader();
        if (btn) {
          btn.classList.remove('is-loading');
          btn.classList.add('is-done');
          setTimeout(() => btn.classList.remove('is-done'), 1600);
        }
        const d = this.drawer();
        // Shopify accepted the item: now (never before) the cover flies to the header bag. Captured before Quick
        // view closes; skipped for Buy now (checkout follows) and when the cart drawer is already open.
        const snap = opts.silent || (d && d.open) ? null : Motion.snapshot(Motion.cover(btn));
        // Quick view closes first: the drawer (or Shiprocket's popup) must not open underneath a modal
        const fromQuick = QuickView.isOpen();
        if (fromQuick) QuickView.close();
        if (snap) await Motion.fly(snap, Motion.target('[data-sk-cart-toggle]'));
        if (cartNow) this.setCount(cartNow.item_count);
        else this.refreshCount();
        // Header bag bounces as the cover lands; the drawer follows a beat later so the bounce is seen
        document.dispatchEvent(new CustomEvent('sakal:cart:added', { detail: { items } }));
        if (snap && !reduceMotion.matches) await new Promise((r) => setTimeout(r, 200));
        if (opts.silent) { /* Buy now continues straight to checkout */ }
        else if (d && document.body.dataset.cartType !== 'page') openDialog(d, fromQuick ? QuickView.trigger : btn);
        else toast('Added to your bag');
        // Buy now goes straight to checkout, so it updates the running total without celebrating
        FreeShip.check(cartNow, opts.silent);
        return true;
      } catch (err) {
        endLoader();
        if (btn) btn.classList.remove('is-loading');
        // A timed-out request may still have reached Shopify: show the bag count as it really is
        if (err.name === 'TimeoutError') this.refreshCount();
        toast(
          err.status === 422 ? err.message
            : err.status === 429 ? 'Too many requests — please wait a moment and try again.'
            : 'Could not add to bag. Please try again.',
          'error'
        );
        return false;
      } finally {
        if (btn) btn.removeAttribute('aria-busy');
      }
    },
    async change(lineEl, quantity) {
      if (this.busy) return;
      this.busy = true;
      const line = Number(lineEl.dataset.skLine);
      const wrap = lineEl.closest('[data-sk-cart-page-inner], #SkCartDrawerContent');
      if (wrap) wrap.classList.add('sk-cart-busy');
      const endLoader = Loader.inline(wrap); // on the busy veil; fresh markup replaces it on success
      const title = $('.sk-cline__title, .sk-line__title', lineEl)?.textContent.trim() || 'Book';
      try {
        const data = await this.request('cart/change.js', { line, quantity });
        await this.render(data.sections);
        this.setCount(data.item_count);
        FreeShip.check(data);
        if (this.pageSection()) {
          const total = $('[data-sk-sum-total]')?.textContent || '';
          this.announce(quantity === 0
            ? `Removed ${title} from your cart.${data.item_count ? ` New total ${total}.` : ' Your cart is empty.'}`
            : `${title}: quantity ${quantity}. New total ${total}.`);
        } else if (quantity === 0) toast('Removed from your bag');
      } catch (err) {
        lineEl.classList.remove('is-leaving');
        if (wrap) wrap.classList.remove('sk-cart-busy');
        const msg = lineEl.querySelector('[data-sk-line-error]');
        if (msg) { msg.textContent = err.message; msg.hidden = false; }
        else toast(err.message, 'error');
        const input = lineEl.querySelector('[data-sk-qty-input]');
        if (input) input.value = input.defaultValue;
      } finally {
        endLoader();
        this.busy = false;
      }
    },
    // Manual code field (drawer + cart page). Kept for its callers; applyCode does the work.
    coupon(code, msgEl, btn) {
      return this.applyCode(code, { btn, scope: msgEl.closest('[data-sk-cart-page], #SkCartDrawer'), field: true });
    },
    // Discount codes go through Shopify's cart (cart/update.js `discount`), so Shopify alone decides whether a code
    // applies. A code Shopify rejects is taken off again (it would otherwise sit on the cart as "not applicable"),
    // and codes that a new one displaced are dropped, so the cart, the summary and checkout always agree.
    // Shiprocket's cart checkout takes its coupon from the discount_code cookie (the cookie Shopify's own
    // /discount/CODE link sets), so it is kept in step with the codes on the cart.
    activeCodes(cart) { return ((cart && cart.discount_codes) || []).filter((d) => d.applicable).map((d) => d.code); },
    syncCodeCookie(codes) {
      document.cookie = codes.length
        ? `discount_code=${encodeURIComponent(codes[codes.length - 1])}; path=/; max-age=1209600; SameSite=Lax`
        : 'discount_code=; path=/; max-age=0; SameSite=Lax';
    },
    // Report a code result where the shopper is looking: the offer card for that code, else the manual field
    // (both found again after the re-render), plus the page's live region (or a toast in the drawer)
    codeMsg(scope, code, text, ok, field) {
      const root = scope && scope.isConnected ? scope : document;
      const card = !field && $(`[data-sk-offer="${CSS.escape(code.toLowerCase())}"] [data-sk-offer-msg]`, root);
      const el = card || $('[data-sk-coupon-msg]', root);
      if (el) {
        el.textContent = text;
        el.hidden = false;
        if (card) el.classList.toggle('is-ok', ok);
        else el.className = `sk-field-msg sk-field-msg--${ok ? 'ok' : 'error'}`;
        const input = !card && $('[data-sk-coupon-input]', root);
        if (input) input.setAttribute('aria-invalid', String(!ok));
      }
      if (this.pageSection()) this.announce(text);
      else if (ok || !el) toast(text, ok ? undefined : 'error');
    },
    async applyCode(raw, { btn, scope, field } = {}) {
      const code = String(raw || '').trim();
      if (!code) return this.codeMsg(scope, '', 'Please enter a code.', false, true);
      if (this.busy) return;
      this.busy = true;
      if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
      const eq = (a) => a.toLowerCase() === code.toLowerCase();
      try {
        const before = this.activeCodes(await this.fetchCart());
        if (before.some(eq)) return this.codeMsg(scope, code, `Code ${code.toUpperCase()} is already applied.`, true, field);
        const data = await this.request('cart/update.js', { discount: [...before, code].join(',') });
        const all = data.discount_codes || [];
        const hit = all.find((d) => eq(d.code));
        if (hit && hit.applicable) {
          const live = this.activeCodes(data);
          const dropped = before.filter((c) => !live.some((l) => l.toLowerCase() === c.toLowerCase()));
          const final = all.some((d) => !d.applicable) ? await this.request('cart/update.js', { discount: live.join(',') }) : data;
          this.syncCodeCookie(this.activeCodes(final));
          await this.render(final.sections);
          FreeShip.check(final);
          this.codeMsg(scope, code, `Code ${hit.code.toUpperCase()} applied.${dropped.length ? ` It replaced ${dropped.join(', ')}, which can’t be combined with it.` : ''}`, true, field);
        } else {
          // Put the cart back exactly as it was (the rejected code is not left behind)
          const back = await this.request('cart/update.js', { discount: before.join(',') });
          this.syncCodeCookie(this.activeCodes(back));
          this.codeMsg(scope, code, `${code.toUpperCase()} can’t be applied to this cart. It may have expired, or the cart may not meet its conditions.`, false, field);
        }
      } catch (err) {
        this.codeMsg(scope, code, err.status === 429 ? 'Too many attempts — please wait a moment and try again.' : 'Could not apply the code. Please check your connection and try again.', false, field);
      } finally {
        this.busy = false;
        if (btn && btn.isConnected) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); }
      }
    },
    async removeCode(code, { btn, scope } = {}) {
      if (this.busy || !code) return;
      this.busy = true;
      if (btn) btn.classList.add('is-loading');
      try {
        const rest = this.activeCodes(await this.fetchCart()).filter((c) => c.toLowerCase() !== code.toLowerCase());
        const data = await this.request('cart/update.js', { discount: rest.join(',') });
        this.syncCodeCookie(this.activeCodes(data));
        await this.render(data.sections);
        FreeShip.check(data);
        this.announce(`Code ${code.toUpperCase()} removed.`);
      } catch (_) {
        this.codeMsg(scope, code, 'Could not remove the code. Please try again.', false);
      } finally {
        this.busy = false;
        if (btn && btn.isConnected) btn.classList.remove('is-loading');
      }
    },
    async clear(btn) {
      if (this.busy) return;
      this.busy = true;
      const wrap = btn.closest('[data-sk-cart-page-inner]');
      if (wrap) wrap.classList.add('sk-cart-busy');
      const endLoader = Loader.inline(wrap);
      try {
        const data = await this.request('cart/clear.js', {});
        await this.render(data.sections);
        this.setCount(0);
        FreeShip.check(data, true);
        this.announce('Your cart is now empty.');
      } catch (_) {
        if (wrap) wrap.classList.remove('sk-cart-busy');
        this.announce('Could not clear the cart. Please try again.', 'error');
      } finally {
        endLoader();
        this.busy = false;
      }
    },
  };

  /* ---------- Cart page: summary placement, mobile checkout bar, offers ---------- */
  // Re-bound after every cart re-render (Cart.swapPage) and on load. Observers live on the module object, so a
  // re-bind disconnects the previous ones first: one observer of each kind, pointing at current nodes.
  const CartPage = {
    io: null,
    ro: null,
    mq: window.matchMedia('(max-width: 989px)'),
    onMq: null,
    bind(scope = document) {
      const page = Cart.pageSection();
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      if (this.onMq) this.mq.removeEventListener('change', this.onMq);
      this.io = this.ro = this.onMq = null;
      document.body.classList.remove('sk-has-cartbar');
      if (!page) return;
      // Tall summary: measured height lets CSS stick it by its bottom edge (sections/sakal-main-cart.liquid)
      const aside = $('[data-sk-cart-aside]', page);
      if (aside && 'ResizeObserver' in window) {
        this.ro = new ResizeObserver(([e]) => aside.style.setProperty('--sk-aside-h', `${Math.ceil(e.borderBoxSize?.[0]?.blockSize ?? aside.offsetHeight)}px`));
        this.ro.observe(aside);
      }
      // Mobile bar: visible only while the real checkout control is off screen (never on top of it)
      const bar = $('[data-sk-cartbar]', page);
      const slot = $('[data-sk-checkout-slot]', page);
      if (!bar || !slot || !('IntersectionObserver' in window)) return;
      // Initial state from geometry now (an observer's first callback can arrive late on a busy page load)
      const r = slot.getBoundingClientRect();
      let slotSeen = r.top < window.innerHeight && r.bottom > 0;
      const update = () => {
        const show = this.mq.matches && !slotSeen;
        bar.hidden = !show;
        document.body.classList.toggle('sk-has-cartbar', show);
      };
      this.io = new IntersectionObserver(([e]) => { slotSeen = e.isIntersecting; update(); });
      this.io.observe(slot);
      this.onMq = update;
      this.mq.addEventListener('change', update);
      update();
    },
    // First load on phones: offers start folded (their summary shows the count); re-renders keep the shopper's choice
    initOffers(scope = document) {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      $$('details[data-sk-offers]', scope).forEach((d) => {
        if (d.dataset.skReady) return;
        d.dataset.skReady = '1';
        d.open = false;
      });
    },
    // The same checkout control the summary shows: Shiprocket's button once its loader has replaced the native
    // one, otherwise the native button[name=checkout] (Shopify checkout). No second checkout button exists.
    checkout() {
      const page = Cart.pageSection();
      if (!page) return;
      const sr = $('shiprocket-headless, .shiprocket-headless', page);
      if (sr) return (sr.querySelector('button, a') || sr).click();
      const native = document.getElementById('SkCartCheckout');
      if (native && !native.disabled) native.click();
    },
  };
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-sk-checkout-proxy]')) CartPage.checkout();
  });
  // Offer cards: Apply (a real /discount/CODE link without JavaScript), Remove, Copy
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-sk-code-apply]');
    if (a) {
      e.preventDefault();
      return Cart.applyCode(a.dataset.skCodeApply, { btn: a, scope: a.closest('[data-sk-cart-page], #SkCartDrawer') });
    }
    const r = e.target.closest('[data-sk-code-remove]');
    if (r) return Cart.removeCode(r.dataset.skCodeRemove, { btn: r, scope: r.closest('[data-sk-cart-page], #SkCartDrawer') });
    const c = e.target.closest('[data-sk-copy]');
    if (c) {
      const label = $('[data-sk-copy-label]', c);
      const done = (text) => {
        if (label) { label.textContent = text; setTimeout(() => { if (label.isConnected) label.textContent = 'Copy'; }, 1800); }
        Cart.announce(text === 'Copied' ? `Code ${c.dataset.skCopy} copied. Paste it at checkout or press Apply.` : 'Could not copy the code.');
      };
      if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(c.dataset.skCopy).then(() => done('Copied'), () => done('Copy failed'));
      else done('Copy failed');
    }
  });
  // Clear cart: a /cart/clear link that asks for a second tap within 4 s before emptying the cart
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-sk-cart-clear]');
    if (!a) return;
    e.preventDefault();
    const label = $('[data-sk-cart-clear-label]', a);
    if (!a.classList.contains('is-confirm')) {
      a.classList.add('is-confirm');
      if (label) label.textContent = 'Tap again to clear cart';
      Cart.announce('Press Clear cart again to remove every book from your cart.');
      clearTimeout(a._skT);
      a._skT = setTimeout(() => { a.classList.remove('is-confirm'); if (label) label.textContent = 'Clear cart'; }, 4000);
      return;
    }
    clearTimeout(a._skT);
    Cart.clear(a);
  });

  /* ---------- Delivery availability (cart page) ---------- */
  // Runs only when the merchant configured a serviceability endpoint (snippets/sakal-delivery-check.liquid).
  // The answer is shown exactly as the endpoint gives it: deliverable / not deliverable, and a date or day range
  // only when one is returned. Anything else (timeout, bad response) is reported as "couldn't check", never as
  // deliverable. The newest request wins; older ones are aborted. Endpoint text is inserted as text, never HTML.
  const DeliveryCheck = {
    ctrl: null,
    key: 'sk-pin',
    render(box, kind, title, lines = []) {
      box.className = `sk-pin__result${kind ? ` is-${kind}` : ''}`;
      box.replaceChildren();
      const t = document.createElement('strong');
      t.textContent = title;
      box.appendChild(t);
      for (const l of lines.filter(Boolean)) { const s = document.createElement('span'); s.textContent = l; box.appendChild(s); }
    },
    estimate(d) {
      const etd = d.etd || d.estimated_delivery_date;
      if (etd) {
        const dt = new Date(etd);
        if (!Number.isNaN(dt.getTime()) && dt > Date.now() - 864e5) return `Estimated delivery by ${dt.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}.`;
      }
      const min = Number(d.min_days);
      const max = Number(d.max_days);
      if (Number.isFinite(min) && min > 0) return `Estimated delivery in ${Number.isFinite(max) && max > min ? `${min}–${max}` : min} ${(max || min) === 1 ? 'day' : 'days'} after dispatch.`;
      return '';
    },
    async check(form) {
      const input = $('[data-sk-pin-input]', form);
      const btn = $('[data-sk-pin-btn]', form);
      const box = form.parentElement && $('[data-sk-pin-msg]', form.parentElement);
      if (!input || !box) return;
      const pin = input.value.replace(/\D/g, '');
      input.value = pin;
      if (!/^[1-9]\d{5}$/.test(pin)) {
        input.setAttribute('aria-invalid', 'true');
        this.render(box, 'error', 'Enter a valid 6-digit PIN code.');
        input.focus();
        return;
      }
      input.setAttribute('aria-invalid', 'false');
      if (this.ctrl) this.ctrl.abort();
      const ctrl = (this.ctrl = new AbortController());
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      this.render(box, '', `Checking delivery to ${pin}…`);
      const inner = $('[data-sk-cart-page-inner]');
      const u = new URL(form.dataset.endpoint, location.origin);
      u.searchParams.set('pincode', pin);
      if (inner) { u.searchParams.set('weight', inner.dataset.weight || '0'); u.searchParams.set('subtotal', inner.dataset.total || '0'); }
      try {
        const res = await fetchT(u.toString(), { headers: { Accept: 'application/json' }, signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = await res.json();
        const place = [d.city, d.state].filter((x) => typeof x === 'string' && x.trim()).join(', ');
        const note = typeof d.message === 'string' ? d.message.slice(0, 200) : '';
        if (d.serviceable === true) {
          this.render(box, 'ok', `Delivery available to ${pin}${place ? ` (${place})` : ''}.`, [this.estimate(d), d.cod === true ? 'Cash on delivery available for this PIN code.' : '', note]);
        } else if (d.serviceable === false) {
          this.render(box, 'no', `Sorry, we can’t deliver to ${pin} at the moment.`, [note || 'Please try another address, or contact us for help.']);
        } else throw new Error('Unexpected response');
        try { localStorage.setItem(this.key, pin); } catch (_) {}
      } catch (err) {
        if (ctrl.signal.aborted && err.name === 'AbortError') return; // superseded by a newer check
        this.render(box, 'error', 'We couldn’t check this PIN code right now.', ['Delivery to your address is confirmed at checkout.']);
      } finally {
        if (this.ctrl === ctrl) { this.ctrl = null; btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); }
      }
    },
    init(scope = document) {
      let saved = '';
      try { saved = localStorage.getItem(this.key) || ''; } catch (_) {}
      if (!/^\d{6}$/.test(saved)) return;
      $$('[data-sk-pin-input]', scope).forEach((i) => { if (!i.value) i.value = saved; });
    },
  };
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-sk-pin]');
    if (!form) return;
    e.preventDefault();
    DeliveryCheck.check(form);
  });
  document.addEventListener('input', (e) => {
    const i = e.target.closest('[data-sk-pin-input]');
    if (i) { const v = i.value.replace(/\D/g, '').slice(0, 6); if (v !== i.value) i.value = v; i.removeAttribute('aria-invalid'); }
  });

  // Add-to-cart: cards (data-sk-add="variantId") and product forms (data-sk-product-form)
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-sk-add]');
    if (!b || b.disabled) return;
    e.preventDefault();
    Cart.add([{ id: Number(b.dataset.skAdd), quantity: Number(b.dataset.qty || 1) }], b);
  });
  // Buy now (cards): add the book, then hand off through the SAME checkout control the cart uses.
  // Shiprocket replaces button[name=checkout] with its own button; we click that, so its one-click flow
  // runs unchanged. If Shiprocket has not attached (script blocked/slow), fall back to Shopify checkout.
  // Returns true when it navigates away (fallback), so the caller can keep the loader up until the page unloads
  async function checkoutNow() {
    const find = () => document.querySelector('#SkCartDrawer shiprocket-headless, #SkCartDrawer .shiprocket-headless, [data-sk-cart-page] shiprocket-headless, [data-sk-cart-page] .shiprocket-headless');
    let sr = find();
    for (let i = 0; !sr && i < 15; i++) { await new Promise((r) => setTimeout(r, 100)); sr = find(); }
    if (sr) { (sr.querySelector('button, a') || sr).click(); return false; }
    window.location.href = url('checkout');
    return true;
  }
  // Brand loader while the book is added and checkout is reached; it steps aside the moment Shiprocket's own
  // button is clicked (its popup shows its own progress) and on any failure. Fallback navigation keeps it until
  // the page unloads (Loader.MAX and the pageshow reset cover a cancelled navigation).
  async function buyAndCheckout(items, btn) {
    const end = Loader.page('Taking you to checkout…');
    let leaving = false;
    try {
      const ok = await Cart.add(items, btn, { silent: true });
      if (ok) leaving = await checkoutNow();
    } finally {
      if (!leaving) end();
    }
  }
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-sk-buy]');
    if (!b || b.disabled || b.dataset.busy) return;
    e.preventDefault();
    b.dataset.busy = '1';
    try {
      await buyAndCheckout([{ id: Number(b.dataset.skBuy), quantity: 1 }], b);
    } finally { delete b.dataset.busy; }
  });
  // One line item from a product form: the explicitly selected variant, quantity and line-item properties
  function formItem(form) {
    const fd = new FormData(form);
    const id = Number(fd.get('id'));
    if (!id) return null;
    const item = { id, quantity: Math.max(1, Number(fd.get('quantity') || 1)) };
    const props = {};
    for (const [k, v] of fd.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m && v) props[m[1]] = v; }
    if (Object.keys(props).length) item.properties = props;
    return item;
  }
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-sk-product-form]');
    if (!form) return;
    e.preventDefault();
    const item = formItem(form);
    if (!item) return toast('Please choose a format.', 'error');
    Cart.add([item], e.submitter || form.querySelector('[type=submit]'));
  });
  // Buy now (book page, sticky bar, Quick view): same hand-off as the cards, with the form's variant + quantity
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-sk-buy-form]');
    if (!b || b.disabled || b.dataset.busy) return;
    e.preventDefault();
    const form = b.dataset.skBuyForm ? document.getElementById(b.dataset.skBuyForm) : b.closest('form');
    const item = form && formItem(form);
    if (!item) return toast('Please choose a format.', 'error');
    b.dataset.busy = '1';
    try {
      await buyAndCheckout([item], b);
    } finally { delete b.dataset.busy; }
  });
  // Notify me (snippets/sakal-buy-box.liquid): Shopify contact form → email to the store. It submits natively so
  // Shopify's own spam protection (hCaptcha, which listens for this submit) runs and sends exactly one request.
  // This capture-phase listener runs first: invalid input is stopped before Shopify's script ever sees it.
  // After the reload, Liquid's form.posted_successfully? renders the thank-you (book page); Quick view lives on
  // another page, so its confirmation is a toast (below).
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-sk-notify-form]');
    if (!form) return;
    const msg = form.querySelector('.sk-notify__msg');
    const btn = form.querySelector('.sk-notify__btn');
    const say = (text, bad) => { if (msg) { msg.textContent = text; msg.classList.toggle('is-error', !!bad); } };
    const fields = [
      [form.querySelector('[name="contact[email]"]'), 'Please enter a valid email address.'],
      [form.querySelector('[name="contact[Mobile]"]'), 'Please enter a valid mobile number (10 digits).'],
    ];
    for (const [input, text] of fields) {
      if (!input) continue;
      input.value = input.value.trim();
      // Mobile: 10–13 digits once spaces, dashes and a leading + are ignored
      const digits = input.value.replace(/\D/g, '').length;
      const ok = input.type === 'tel' ? /^\+?[\d\s-]+$/.test(input.value) && digits >= 10 && digits <= 13 : input.checkValidity();
      input.setAttribute('aria-invalid', String(!ok));
      if (!ok) {
        e.preventDefault();
        e.stopImmediatePropagation();
        say(text, true);
        input.focus();
        return;
      }
    }
    if (btn && btn.classList.contains('is-loading')) { e.preventDefault(); e.stopImmediatePropagation(); return; } // no double send
    say('Sending your request…');
    if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
    // Remember where the request came from, so the page that reloads can confirm it
    try { sessionStorage.setItem('sk-notify-sent', form.id); } catch (_) {}
  }, true);
  // After the reload: confirm a Quick-view request (its form is not on this page) and bring a page form into view
  (() => {
    if (!/[?&]contact_posted=true/.test(location.search)) return;
    let sent = '';
    try { sent = sessionStorage.getItem('sk-notify-sent') || ''; sessionStorage.removeItem('sk-notify-sent'); } catch (_) {}
    if (!sent) return;
    const here = document.getElementById(sent);
    const ok = here && here.querySelector('.sk-notify__ok');
    if (ok) ok.closest('[data-sk-notify]')?.scrollIntoView({ block: 'center' });
    else toast("Thank you! We'll let you know when the book is back in stock.");
  })();
  // Add-to-bag feedback: the header bag gives a short shake and its count pops
  document.addEventListener('sakal:cart:added', () => {
    if (reduceMotion.matches) return;
    $$('[data-sk-cart-toggle]').forEach((el) => {
      el.classList.remove('sk-bag-bump');
      void el.offsetWidth; // restart the animation
      el.classList.add('sk-bag-bump');
      setTimeout(() => el.classList.remove('sk-bag-bump'), 900);
    });
  });
  // Header cart icon opens the drawer (falls back to /cart link without JS)
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-sk-cart-toggle]');
    if (!a) return;
    const d = Cart.drawer();
    if (!d) return;
    e.preventDefault();
    openDialog(d, a);
  });
  // Quantity + remove
  document.addEventListener('click', (e) => {
    const q = e.target.closest('[data-sk-qty]');
    const r = e.target.closest('[data-sk-remove]');
    const lineEl = (q || r) && (q || r).closest('[data-sk-line]');
    if (!lineEl) return;
    if (r) e.preventDefault(); // the cart page's Remove is a real /cart/change link without JavaScript
    if (Cart.busy) return; // one cart update at a time: the shown number never runs ahead of the cart
    if (r) {
      if (!reduceMotion.matches) lineEl.classList.add('is-leaving');
      return Cart.change(lineEl, 0);
    }
    const input = lineEl.querySelector('[data-sk-qty-input]');
    const next = Math.max(0, Number(input.value) + Number(q.dataset.skQty));
    input.value = next;
    Cart.change(lineEl, next);
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-sk-qty-input]');
    const lineEl = input && input.closest('[data-sk-line]');
    if (!lineEl) return;
    if (Cart.busy) { input.value = input.defaultValue; return; }
    Cart.change(lineEl, Math.max(0, parseInt(input.value, 10) || 0));
  });
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-sk-coupon-apply]');
    if (!b) return;
    const box = b.closest('[data-sk-coupon]');
    Cart.coupon(box.querySelector('[data-sk-coupon-input]').value.trim(), box.querySelector('[data-sk-coupon-msg]'), b);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.matches('[data-sk-coupon-input]')) {
      e.preventDefault();
      e.target.closest('[data-sk-coupon]').querySelector('[data-sk-coupon-apply]').click();
    }
  });
  // Keep the header count honest when returning via bfcache
  window.addEventListener('pageshow', async (e) => { if (e.persisted) FreeShip.check(await Cart.refreshCount(), true); });

  /* ---------- Header: sticky state, mega menu ---------- */
  function initHeader(scope = document) {
    const header = $('[data-sk-header]', scope);
    if (!header || header.dataset.skReady) return;
    header.dataset.skReady = '1';
    const bar = $('.sk-header', header);
    const onScroll = () => bar && bar.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    // Write the root variable only when the height actually changes (a root custom-property write
    // invalidates style for the whole document).
    let lastH = -1;
    const setH = (h) => { if (h !== lastH) { lastH = h; document.documentElement.style.setProperty('--sk-header-total', h + 'px'); } };
    new ResizeObserver(([e]) => setH(Math.round(e.borderBoxSize ? e.borderBoxSize[0].blockSize : e.contentRect.height))).observe(header);

    const triggers = $$('[data-sk-mega-trigger]', header);
    let openId = null, hoverTimer;
    const close = () => {
      if (!openId) return;
      const t = header.querySelector(`[aria-controls="${openId}"]`);
      const p = document.getElementById(openId);
      if (t) t.setAttribute('aria-expanded', 'false');
      if (p) p.classList.remove('is-open');
      openId = null;
    };
    const open = (t) => {
      const id = t.getAttribute('aria-controls');
      if (openId === id) return;
      close();
      t.setAttribute('aria-expanded', 'true');
      document.getElementById(id).classList.add('is-open');
      openId = id;
    };
    triggers.forEach((t) => {
      t.addEventListener('click', () => (t.getAttribute('aria-expanded') === 'true' ? close() : open(t)));
      const item = t.closest('[data-sk-mega-item]');
      if (window.matchMedia('(hover: hover)').matches) {
        item.addEventListener('mouseenter', () => { clearTimeout(hoverTimer); hoverTimer = setTimeout(() => open(t), 120); });
        item.addEventListener('mouseleave', () => { clearTimeout(hoverTimer); hoverTimer = setTimeout(close, 180); });
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && openId) {
        const t = header.querySelector(`[aria-controls="${openId}"]`);
        close();
        if (t) t.focus();
      }
    });
    document.addEventListener('click', (e) => { if (openId && !e.target.closest('[data-sk-mega-item]')) close(); });
    header.addEventListener('focusout', (e) => {
      if (openId && e.relatedTarget && !e.relatedTarget.closest('[data-sk-mega-item]')) close();
    });
  }
  // Mobile menu accordions
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-sk-acc]');
    if (!t) return;
    const p = document.getElementById(t.getAttribute('aria-controls'));
    const exp = t.getAttribute('aria-expanded') === 'true';
    t.setAttribute('aria-expanded', String(!exp));
    if (p) p.hidden = exp;
  });
  // Announcement dismiss
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-sk-announce-close]');
    if (!b) return;
    const bar = b.closest('[data-sk-announce]');
    bar.hidden = true;
    try { sessionStorage.setItem('sk-announce-' + bar.dataset.key, '1'); } catch (_) {}
  });
  function initAnnounce() {
    $$('[data-sk-announce]').forEach((bar) => {
      try { if (sessionStorage.getItem('sk-announce-' + bar.dataset.key)) bar.hidden = true; } catch (_) {}
    });
    // Rotator: cycles messages; pauses on hover/focus and via the toggle; no autoplay under reduced motion.
    $$('[data-sk-rotator]').forEach((rot) => {
      if (rot.dataset.skReady) return;
      rot.dataset.skReady = '1';
      const slides = $$('[data-sk-rot-slide]', rot);
      if (slides.length < 2) return;
      const toggle = $('[data-sk-rot-toggle]', rot);
      let i = 0, timer = 0, held = false, paused = reduceMotion.matches;
      const show = (n) => {
        slides[i].hidden = true; slides[i].classList.remove('is-active', 'is-in');
        i = (n + slides.length) % slides.length;
        slides[i].hidden = false; slides[i].classList.add('is-active', 'is-in');
      };
      const stop = () => { clearInterval(timer); timer = 0; };
      const start = () => { stop(); if (!paused && !held) timer = setInterval(() => show(i + 1), Number(rot.dataset.interval) || 5000); };
      const sync = () => { if (toggle) { toggle.setAttribute('aria-pressed', String(paused)); toggle.setAttribute('aria-label', paused ? 'Play announcements' : 'Pause announcements'); } start(); };
      $('[data-sk-rot-next]', rot)?.addEventListener('click', () => { show(i + 1); start(); });
      $('[data-sk-rot-prev]', rot)?.addEventListener('click', () => { show(i - 1); start(); });
      toggle?.addEventListener('click', () => { paused = !paused; sync(); });
      rot.addEventListener('pointerenter', () => { held = true; stop(); });
      rot.addEventListener('pointerleave', () => { held = false; start(); });
      rot.addEventListener('focusin', () => { held = true; stop(); });
      rot.addEventListener('focusout', () => { held = false; start(); });
      document.addEventListener('shopify:section:unload', (e) => { if (e.target.contains(rot)) stop(); });
      sync();
    });
  }

  /* ---------- Slideshow (sections/sakal-slideshow.liquid) ---------- */
  // Two modes: "scroll" (classic, peek — scroll-snap track, native swipe) and "fade" (cinematic, split — stacked
  // slides, pointer swipe). Shared: arrows, dots/thumbnails, counter + timing bar, split panel text, autoplay.
  function initSlideshows(scope = document) {
    $$('[data-sk-slideshow]', scope).forEach((ss) => {
      if (ss.dataset.skReady) return;
      ss.dataset.skReady = '1';
      const track = $('[data-sk-ss-track]', ss);
      const slides = $$('[data-sk-ss-slide]', ss);
      if (!track || slides.length < 2) return;
      const fade = ss.dataset.mode === 'fade';
      const dots = $$('[data-sk-ss-dot]', ss);
      const panels = $$('[data-sk-ss-panel]', ss);
      const counts = $$('[data-sk-ss-count]', ss);
      const toggle = $('[data-sk-ss-toggle]', ss);
      const ac = new AbortController();
      const opt = { signal: ac.signal };
      let i = 0, timer = 0, held = false, paused = reduceMotion.matches || ss.dataset.autoplay !== 'true';
      const pad = (n) => String(n).padStart(2, '0');
      const restartBar = () => { ss.classList.remove('is-playing'); void ss.offsetWidth; if (timer) ss.classList.add('is-playing'); };
      const sync = () => {
        slides.forEach((s, k) => { const on = k === i; s.classList.toggle('is-active', on); s.inert = !on; });
        dots.forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
        panels.forEach((p, k) => { const on = k === i; p.hidden = !on; p.classList.toggle('is-active', on); });
        counts.forEach((c) => (c.textContent = pad(i + 1)));
        const t = dots[i]; if (t && t.classList.contains('sk-ss__thumb')) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      };
      const go = (n) => {
        i = (n + slides.length) % slides.length;
        if (fade) sync();
        else {
          const s = slides[i];
          track.scrollTo({ left: s.offsetLeft - track.offsetLeft - (track.clientWidth - s.clientWidth) / 2, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        }
        restartBar();
      };
      const stop = () => { clearInterval(timer); timer = 0; ss.classList.remove('is-playing'); };
      const start = () => { stop(); if (!paused && !held && !document.hidden) { timer = setInterval(() => go(i + 1), Number(ss.dataset.interval) || 6000); restartBar(); } };
      if (!fade) {
        // scroll mode: the slide nearest the track centre is active
        track.addEventListener('scroll', () => requestAnimationFrame(() => {
          const mid = track.scrollLeft + track.clientWidth / 2;
          let best = 0, dist = Infinity;
          slides.forEach((s, k) => { const d = Math.abs(s.offsetLeft - track.offsetLeft + s.clientWidth / 2 - mid); if (d < dist) { dist = d; best = k; } });
          if (best !== i) { i = best; restartBar(); }
          sync();
        }), { passive: true, ...opt });
      } else {
        // fade mode: horizontal swipe changes slides; taps still follow the slide link
        let x0 = null, swiped = false;
        track.addEventListener('pointerdown', (e) => { x0 = e.clientX; swiped = false; }, opt);
        track.addEventListener('pointerup', (e) => {
          if (x0 === null) return;
          const dx = e.clientX - x0; x0 = null;
          if (Math.abs(dx) > 40) { swiped = true; go(i + (dx < 0 ? 1 : -1)); start(); }
        }, opt);
        track.addEventListener('click', (e) => { if (swiped) { e.preventDefault(); swiped = false; } }, opt);
      }
      $$('[data-sk-ss-prev]', ss).forEach((b) => b.addEventListener('click', () => { go(i - 1); start(); }, opt));
      $$('[data-sk-ss-next]', ss).forEach((b) => b.addEventListener('click', () => { go(i + 1); start(); }, opt));
      dots.forEach((d) => d.addEventListener('click', () => { go(Number(d.dataset.skSsDot)); start(); }, opt));
      ss.addEventListener('keydown', (e) => {
        if (e.target.closest('input, textarea')) return;
        if (e.key === 'ArrowRight') { go(i + 1); start(); } else if (e.key === 'ArrowLeft') { go(i - 1); start(); }
      }, opt);
      toggle?.addEventListener('click', () => {
        paused = !paused;
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
        start();
      }, opt);
      if (toggle && paused) { toggle.setAttribute('aria-pressed', 'true'); toggle.setAttribute('aria-label', 'Play slideshow'); }
      ['pointerenter', 'focusin'].forEach((ev) => ss.addEventListener(ev, () => { held = true; stop(); }, opt));
      ['pointerleave', 'focusout'].forEach((ev) => ss.addEventListener(ev, () => { held = false; start(); }, opt));
      document.addEventListener('visibilitychange', start, opt);
      document.addEventListener('shopify:section:unload', (e) => { if (e.target.contains(ss)) { stop(); ac.abort(); } }, opt);
      if (!fade && ss.closest('.sk-ss--style-peek')) requestAnimationFrame(() => { track.scrollLeft = 0; });
      sync();
      start();
    });
  }

  /* ---------- Shortcut spotlight (sections/sakal-shortcuts.liquid, layout "spotlight") ---------- */
  // ARIA tabs: click/Enter selects; arrow keys move; hover previews on pointer devices.
  function initSpotlight(scope = document) {
    $$('[data-sk-spot]', scope).forEach((spot) => {
      if (spot.dataset.skReady) return;
      spot.dataset.skReady = '1';
      const tabs = $$('[data-sk-spot-tab]', spot);
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (!panel) return;
          if (on && panel.hidden) { panel.hidden = false; panel.classList.remove('is-in'); void panel.offsetWidth; panel.classList.add('is-in'); }
          else if (!on) panel.hidden = true;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((t, k) => {
        t.addEventListener('click', () => select(t));
        t.addEventListener('keydown', (e) => {
          const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
          if (e.key === 'Home') { e.preventDefault(); select(tabs[0], true); }
          else if (e.key === 'End') { e.preventDefault(); select(tabs[tabs.length - 1], true); }
          else if (step) { e.preventDefault(); select(tabs[(k + step + tabs.length) % tabs.length], true); }
        });
        if (window.matchMedia('(hover: hover)').matches) t.addEventListener('pointerenter', () => select(t));
      });
    });
  }

  /* ---------- Rails (scroll-snap carousels) ---------- */
  const railQueue = new Set();
  let railFrame = 0;
  function railBatch(r) {
    railQueue.add(r);
    if (railFrame) return;
    railFrame = requestAnimationFrame(() => {
      railFrame = 0;
      const items = [...railQueue];
      railQueue.clear();
      items.forEach((x) => x.read());
      items.forEach((x) => x.write());
    });
  }
  function initRails(scope = document) {
    $$('[data-sk-rail]', scope).forEach((rail) => {
      if (rail.dataset.skReady) return;
      rail.dataset.skReady = '1';
      const track = $('.sk-rail__track', rail);
      const prev = $('[data-sk-prev]', rail.closest('section') || rail);
      const next = $('[data-sk-next]', rail.closest('section') || rail);
      const bar = $('.sk-rail__progress span', rail);
      if (!track) return;
      const step = () => {
        const first = track.children[0];
        return first ? (first.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0)) * Math.max(1, Math.floor(track.clientWidth / first.getBoundingClientRect().width) - 0) : track.clientWidth;
      };
      // All rails share one batched frame: every rail reads geometry first, then all write
      // (no read/write interleaving → a single layout per frame, however many rails).
      let lastW = -1;
      const geom = {
        read() { this.sw = track.scrollWidth; this.cw = track.clientWidth; this.sl = track.scrollLeft; },
        write() {
          const { sw, cw, sl } = this;
          const max = sw - cw;
          const vis = sw ? Math.min(1, cw / sw) : 1;
          if (prev) prev.disabled = sl <= 4;
          if (next) next.disabled = sl >= max - 4;
          if (bar) {
            bar.style.width = vis * 100 + '%';
            bar.style.transform = `translateX(${max > 0 ? (sl / max) * ((1 - vis) / vis) * 100 : 0}%)`;
          }
        },
      };
      const schedule = () => railBatch(geom);
      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      track.addEventListener('scroll', schedule, { passive: true });
      new ResizeObserver(([entry]) => {
        const w = Math.round(entry.contentRect.width);
        if (w !== lastW) { lastW = w; schedule(); } // height changes (images loading) don't affect the rail
      }).observe(track);
      schedule();
    });
  }

  /* ---------- Hero slideshow ---------- */
  function initHero(scope = document) {
    $$('[data-sk-hero]', scope).forEach((hero) => {
      if (hero.dataset.skReady) return;
      hero.dataset.skReady = '1';
      const slides = $$('[data-sk-slide]', hero);
      const dots = $$('[data-sk-dot]', hero);
      const toggle = $('[data-sk-hero-toggle]', hero);
      if (slides.length < 2) return;
      let i = 0, timer = null;
      const delay = Number(hero.dataset.delay || 7) * 1000;
      let playing = hero.dataset.autoplay === 'true' && !reduceMotion.matches;
      const warm = (n) => { const s = slides[n]; if (s) $$('img[loading=lazy]', s).forEach((im) => (im.loading = 'eager')); };
      const go = (n, user) => {
        const prevI = i;
        i = (n + slides.length) % slides.length;
        if (prevI === i) return;
        slides.forEach((s, k) => {
          const on = k === i;
          s.classList.toggle('is-active', on);
          s.setAttribute('aria-hidden', String(!on));
          s.inert = !on;
        });
        dots.forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
        warm(i + 1);
        if (user) restart();
      };
      const tick = () => go(i + 1);
      const restart = () => { clearInterval(timer); if (playing) timer = setInterval(tick, delay); };
      const setPlaying = (p) => {
        playing = p;
        if (toggle) {
          toggle.setAttribute('aria-label', p ? 'Pause slideshow' : 'Play slideshow');
          toggle.dataset.state = p ? 'playing' : 'paused';
        }
        restart();
      };
      $$('[data-sk-hero-prev]', hero).forEach((b) => b.addEventListener('click', () => go(i - 1, true)));
      $$('[data-sk-hero-next]', hero).forEach((b) => b.addEventListener('click', () => go(i + 1, true)));
      dots.forEach((d, k) => d.addEventListener('click', () => go(k, true)));
      if (toggle) toggle.addEventListener('click', () => setPlaying(!playing));
      hero.addEventListener('mouseenter', () => clearInterval(timer));
      hero.addEventListener('mouseleave', restart);
      hero.addEventListener('focusin', () => clearInterval(timer));
      hero.addEventListener('focusout', restart);
      let x0 = null;
      hero.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
      hero.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) go(i + (dx < 0 ? 1 : -1), true);
        x0 = null;
      });
      document.addEventListener('visibilitychange', () => (document.hidden ? clearInterval(timer) : restart()));
      warm(1);
      setPlaying(playing);
    });
  }

  /* ---------- Video facades (click to play, no autoload) ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-sk-video]');
    if (!b) return;
    e.preventDefault();
    const src = b.dataset.skVideo;
    const wrap = b.closest('[data-sk-video-wrap]') || b.parentElement;
    if (/youtube\.com|youtu\.be/.test(src)) {
      const id = (src.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{11})/) || [])[1];
      if (!id) return window.open(src, '_blank', 'noopener');
      const f = document.createElement('iframe');
      f.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`;
      f.allow = 'autoplay; encrypted-media; picture-in-picture';
      f.allowFullscreen = true;
      f.title = b.getAttribute('aria-label') || 'Video';
      f.className = 'sk-video__el';
      wrap.replaceChildren(f);
      return;
    }
    const v = document.createElement('video');
    v.src = src;
    v.controls = true;
    v.playsInline = true;
    v.autoplay = true;
    v.preload = 'auto';
    v.className = 'sk-video__el';
    if (b.dataset.poster) v.poster = b.dataset.poster;
    v.setAttribute('aria-label', b.getAttribute('aria-label') || 'Video');
    v.addEventListener('error', () => {
      wrap.innerHTML = '<p class="sk-video__error">This video can’t be played right now.</p>';
    });
    $$('video.sk-video__el').forEach((o) => o !== v && o.pause());
    wrap.replaceChildren(v);
    v.play().catch(() => {});
  });

  /* ---------- Lazy collection tabs ---------- */
  function initTabs(scope = document) {
    $$('[data-sk-tabs]', scope).forEach((tabs) => {
      if (tabs.dataset.skReady) return;
      tabs.dataset.skReady = '1';
      const btns = $$('[role=tab]', tabs);
      const panel = $('[data-sk-tabs-panel]', tabs);
      const cache = new Map();
      const limit = tabs.dataset.limit || 8;
      let ctrl;
      cache.set(btns[0] && btns[0].dataset.handle, panel.innerHTML);
      const activate = async (btn, focus) => {
        btns.forEach((b) => { const on = b === btn; b.setAttribute('aria-selected', String(on)); b.tabIndex = on ? 0 : -1; });
        if (focus) btn.focus();
        panel.setAttribute('aria-labelledby', btn.id);
        const h = btn.dataset.handle;
        const more = $('[data-sk-tabs-more]', tabs);
        if (more) { more.href = btn.dataset.url; more.querySelector('span').textContent = btn.textContent.trim(); }
        if (cache.has(h)) { panel.innerHTML = cache.get(h); initRails(panel); document.dispatchEvent(new CustomEvent('sakal:content:changed')); return; }
        if (ctrl) ctrl.abort();
        const mine = (ctrl = new AbortController());
        panel.setAttribute('aria-busy', 'true');
        panel.style.minHeight = panel.offsetHeight + 'px';
        const endLoader = Loader.inline(panel);
        try {
          const res = await fetchT(`${url('collections/' + h)}?section_id=sakal-collection-fragment&sk_limit=${limit}`, { signal: mine.signal });
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const frag = doc.querySelector('[data-sk-fragment]');
          const html = frag ? frag.innerHTML : '<p class="sk-meta">No books found.</p>';
          cache.set(h, html);
          if (btn.getAttribute('aria-selected') === 'true') { panel.innerHTML = html; initRails(panel); document.dispatchEvent(new CustomEvent('sakal:content:changed')); }
        } catch (err) {
          if (err.name !== 'AbortError') panel.innerHTML = `<p class="sk-meta">Couldn’t load this shelf. <a class="sk-link" href="${btn.dataset.url}">Open collection</a></p>`;
        } finally {
          endLoader();
          // A shelf request superseded by a newer tab leaves the busy state to the newer one
          if (ctrl === mine) {
            panel.removeAttribute('aria-busy');
            panel.style.minHeight = '';
          }
        }
      };
      btns.forEach((b, k) => {
        b.addEventListener('click', () => activate(b));
        b.addEventListener('keydown', (e) => {
          const n = e.key === 'ArrowRight' ? k + 1 : e.key === 'ArrowLeft' ? k - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? btns.length - 1 : null;
          if (n === null) return;
          e.preventDefault();
          activate(btns[(n + btns.length) % btns.length], true);
        });
      });
    });
  }

  /* ---------- Product page: variant selection, qty, gallery, sticky ATC ---------- */
  function initProduct(scope = document) {
    $$('[data-sk-product]', scope).forEach((pdp) => {
      if (pdp.dataset.skReady) return;
      pdp.dataset.skReady = '1';
      // Everything is scoped to this product root, so the book page and an open Quick view never update each other
      const json = $('script[data-sk-variants]', pdp);
      const variants = json ? JSON.parse(json.textContent) : [];
      const form = $('form[data-sk-product-form]', pdp);
      const idInput = form && $('input[name=id]', form);
      const sticky = $('[data-sk-sticky-atc]', pdp);
      const isQuick = pdp.hasAttribute('data-sk-qv-root');
      const money = (cents) => {
        try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: pdp.dataset.currency || 'INR', maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100); }
        catch (_) { return '₹' + (cents / 100).toFixed(2); }
      };
      const apply = (v) => {
        if (!v || !idInput) return;
        idInput.value = v.id;
        $$('[data-sk-price-now]', pdp).forEach((el) => (el.textContent = money(v.price)));
        $$('[data-sk-price-was]', pdp).forEach((el) => { el.hidden = !(v.compare_at_price > v.price); el.textContent = v.compare_at_price ? money(v.compare_at_price) : ''; });
        $$('[data-sk-save]', pdp).forEach((el) => {
          const on = v.compare_at_price > v.price;
          el.hidden = !on;
          el.textContent = on ? `Save ${Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price)}%` : '';
        });
        $$('[data-sk-deal]', pdp).forEach((el) => {
          const on = v.compare_at_price > v.price;
          el.hidden = !on;
          if (!on) return;
          const pct = $('[data-sk-deal-pct]', el), amt = $('[data-sk-deal-amt]', el);
          if (pct) pct.textContent = Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price);
          if (amt) amt.textContent = money(v.compare_at_price - v.price);
        });
        $$('[data-sk-stock]', pdp).forEach((el) => {
          el.textContent = v.available ? 'In stock' : 'Out of stock';
          el.classList.toggle('is-out', !v.available);
        });
        $$('[data-sk-atc]', pdp).forEach((b) => {
          b.disabled = !v.available;
          const l = b.querySelector('[data-sk-atc-label]');
          if (l) l.textContent = v.available ? (b.dataset.labelAdd || 'Add to bag') : 'Sold out';
          if (b.dataset.ariaAdd) b.setAttribute('aria-label', v.available ? b.dataset.ariaAdd : 'Sold out');
        });
        $$('[data-sk-variant-meta]', pdp).forEach((el) => (el.hidden = el.dataset.skVariantMeta !== String(v.id)));
        // Notify-me appears only for a sold-out format and records which format was asked for
        $$('[data-sk-notify]', pdp).forEach((el) => {
          el.hidden = v.available;
          const f = el.querySelector('[data-sk-notify-variant]');
          if (f) f.value = v.title || '';
        });
        if (v.featured_media_id) {
          const thumb = pdp.querySelector(`[data-sk-thumb="${v.featured_media_id}"]`);
          if (thumb) thumb.click();
        }
        if (isQuick) return; // Quick view never rewrites the page URL
        const u = new URL(location.href);
        u.searchParams.set('variant', v.id);
        history.replaceState(history.state, '', u);
      };
      $$('input[data-sk-option]', pdp).forEach((r) => r.addEventListener('change', () => {
        const chosen = $$('input[data-sk-option]:checked', pdp).map((x) => x.value);
        apply(variants.find((v) => v.options.every((o, k) => o === chosen[k])));
      }));
      // qty stepper (mirrors stay inside this product root)
      $$('[data-sk-step]', pdp).forEach((b) => b.addEventListener('click', () => {
        const input = b.parentElement.querySelector('input');
        const max = Number(input.max) || 99;
        input.value = Math.min(max, Math.max(1, Number(input.value) + Number(b.dataset.skStep)));
        $$('[data-sk-qty-mirror]', pdp).forEach((m) => (m.value = input.value));
      }));
      $$('[data-sk-gal]', pdp).forEach(initGallery);
      initAttention(pdp);
      // sticky ATC: show after the main ATC leaves the viewport, hide near the footer
      const mainAtc = form && $('[data-sk-atc]', form);
      if (sticky && mainAtc && 'IntersectionObserver' in window) {
        let pastAtc = false, atFooter = false;
        const sync = () => {
          const on = pastAtc && !atFooter;
          sticky.classList.toggle('is-visible', on);
          sticky.inert = !on;
          document.body.classList.toggle('sk-has-sticky-atc', on);
        };
        new IntersectionObserver(([e]) => { pastAtc = !e.isIntersecting && e.boundingClientRect.top < 0; sync(); }).observe(mainAtc);
        const footer = $('footer');
        if (footer) new IntersectionObserver(([e]) => { atFooter = e.isIntersecting; sync(); }).observe(footer);
        sticky.inert = true;
        $('[data-sk-sticky-submit]', sticky)?.addEventListener('click', (e) => form.requestSubmit(form.querySelector('[type=submit]') || undefined));
      }
      // description "read more"
      $$('[data-sk-more]', pdp).forEach((b) => b.addEventListener('click', () => {
        const box = document.getElementById(b.getAttribute('aria-controls'));
        const exp = b.getAttribute('aria-expanded') === 'true';
        b.setAttribute('aria-expanded', String(!exp));
        box.classList.toggle('is-open', !exp);
        b.querySelector('span').textContent = exp ? 'Read more' : 'Read less';
      }));
      // "Look inside" viewer: native <dialog> owns Escape and focus return
      const peek = $('[data-sk-peek-dialog]', pdp);
      if (peek && typeof peek.showModal === 'function') {
        const img = $('[data-sk-peek-img]', peek);
        $$('[data-sk-peek]', pdp).forEach((b) => b.addEventListener('click', () => {
          img.src = b.dataset.skPeek;
          img.alt = b.dataset.skPeekAlt || '';
          peek.showModal();
        }));
        $('[data-sk-peek-close]', peek)?.addEventListener('click', () => peek.close());
        peek.addEventListener('click', (e) => { if (e.target === peek) peek.close(); });
      }
      // Discovery layout: chip navigation marks the section currently being read
      const chipnav = $('[data-sk-chipnav]', pdp);
      if (chipnav && 'IntersectionObserver' in window) {
        const list = $('ol', chipnav);
        const links = new Map($$('a[href^="#"]', chipnav).map((a) => [a.getAttribute('href').slice(1), a]));
        const io = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (!en.isIntersecting) return;
            const a = links.get(en.target.id);
            if (!a) return;
            links.forEach((x) => x.removeAttribute('aria-current'));
            a.setAttribute('aria-current', 'location');
            list.scrollTo({ left: a.offsetLeft - 16, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
          });
        }, { rootMargin: '-35% 0px -60% 0px' });
        links.forEach((a, id) => { const t = document.getElementById(id); if (t) io.observe(t); });
      }
    });
  }

  /* ---------- Book media gallery (snippets/sakal-gallery.liquid) ---------- */
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  function initGallery(gal) {
    if (gal.dataset.skReady) return;
    gal.dataset.skReady = '1';
    const main = $('[data-sk-gallery-main]', gal);
    if (!main) return;
    const slides = $$('.sk-gallery__slide', main);
    const thumbs = $$('[data-sk-thumb]', gal);
    const dots = $$('[data-sk-gallery-dot]', gal);
    const current = $('[data-sk-gal-current]', gal);
    const prev = $('[data-sk-gal-prev]', gal);
    const next = $('[data-sk-gal-next]', gal);
    const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');
    let index = -1;
    const go = (i) => {
      const s = slides[Math.max(0, Math.min(slides.length - 1, i))];
      if (s) main.scrollTo({ left: main.scrollLeft + s.getBoundingClientRect().left - main.getBoundingClientRect().left, behavior: behavior() });
    };
    const restorePoster = (slide) => {
      if (slide.dataset.poster && slide.querySelector('iframe')) slide.innerHTML = slide.dataset.poster;
    };
    const sync = () => {
      const i = Math.round(Math.abs(main.scrollLeft) / (main.clientWidth || 1));
      if (i === index) return;
      index = i;
      const id = slides[i] && slides[i].dataset.skMedia;
      thumbs.forEach((t) => t.setAttribute('aria-current', String(t.dataset.skThumb === id)));
      dots.forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
      if (current) current.textContent = i + 1;
      if (prev) prev.disabled = i <= 0;
      if (next) next.disabled = i >= slides.length - 1;
      // Leaving a slide stops its media (video pauses; embedded players go back to their poster)
      slides.forEach((s, k) => {
        if (k === i) return;
        s.querySelectorAll('video').forEach((v) => v.pause());
        restorePoster(s);
      });
    };
    let frame = 0;
    main.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; sync(); }); }, { passive: true });
    sync();
    prev?.addEventListener('click', () => go(index - 1));
    next?.addEventListener('click', () => go(index + 1));
    main.addEventListener('keydown', (e) => {
      if (e.target !== main) return;
      const rtl = getComputedStyle(main).direction === 'rtl';
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        go(index + ((e.key === 'ArrowRight') !== rtl ? 1 : -1));
      }
    });
    thumbs.forEach((t) => t.addEventListener('click', () => go(slides.findIndex((s) => s.dataset.skMedia === t.dataset.skThumb))));
    // External video: click-to-play swaps the poster for the player (with sound, started by the user)
    gal.addEventListener('click', (e) => {
      const p = e.target.closest('[data-sk-ext]');
      if (!p) return;
      const slide = p.closest('.sk-gallery__slide');
      slide.dataset.poster = slide.innerHTML;
      const f = document.createElement('iframe');
      f.className = 'sk-gal__frame';
      f.src = p.dataset.skExt;
      f.title = p.dataset.skExtTitle || 'Video';
      f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      f.allowFullscreen = true;
      slide.replaceChildren(f);
      f.focus();
    });

    // Lightbox
    const lb = $('[data-sk-lightbox]', gal);
    const zooms = $$('[data-sk-zoom]', gal);
    if (lb && zooms.length && typeof lb.showModal === 'function') {
      const img = $('[data-sk-lb-img]', lb);
      const stage = $('[data-sk-lb-stage]', lb);
      const count = $('[data-sk-lb-count]', lb);
      let li = 0;
      const show = (i) => {
        li = (i + zooms.length) % zooms.length;
        const z = zooms[li];
        stage.classList.remove('is-zoomed');
        img.src = z.dataset.skFull;
        img.alt = z.dataset.skAlt || '';
        count.textContent = `Image ${li + 1} of ${zooms.length}`;
      };
      const open = (i) => { show(i); lb.showModal(); document.body.classList.add('sk-lock'); };
      zooms.forEach((z, k) => z.addEventListener('click', () => open(k)));
      $('[data-sk-gal-zoom]', gal)?.addEventListener('click', () => {
        const z = slides[index] && slides[index].querySelector('[data-sk-zoom]');
        open(z ? zooms.indexOf(z) : 0);
      });
      $('[data-sk-lb-prev]', lb)?.addEventListener('click', () => show(li - 1));
      $('[data-sk-lb-next]', lb)?.addEventListener('click', () => show(li + 1));
      $('[data-sk-lb-close]', lb)?.addEventListener('click', () => lb.close());
      lb.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); show(li + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); show(li - 1); }
      });
      stage.addEventListener('click', (e) => {
        if (e.target === stage && !stage.classList.contains('is-zoomed')) { lb.close(); return; }
        if (e.target !== img) return;
        const r = img.getBoundingClientRect();
        const fx = (e.clientX - r.left) / r.width;
        const fy = (e.clientY - r.top) / r.height;
        const zoomed = stage.classList.toggle('is-zoomed');
        if (zoomed) requestAnimationFrame(() => {
          stage.scrollLeft = fx * img.offsetWidth - stage.clientWidth / 2;
          stage.scrollTop = fy * img.offsetHeight - stage.clientHeight / 2;
        });
      });
      lb.addEventListener('close', () => {
        stage.classList.remove('is-zoomed');
        if (!document.querySelector('dialog[open]')) document.body.classList.remove('sk-lock');
        go(slides.findIndex((s) => s.contains(zooms[li])));
      });
    }

    // Magnifier: in-place lens on fine pointers, only where the real image has the resolution for it
    $$('[data-sk-magnify]', gal).forEach((btn) => {
      const img = $('img', btn);
      const fullW = Number(btn.dataset.skFullW) || 0;
      let lens = null;
      const factor = () => Math.min(2.5, fullW / (img.clientWidth || 1));
      btn.addEventListener('pointerenter', (e) => {
        if (!finePointer.matches || e.pointerType !== 'mouse' || factor() < 1.4) return;
        if (!lens) {
          lens = document.createElement('span');
          lens.className = 'sk-gal__lens';
          lens.setAttribute('aria-hidden', 'true');
          lens.style.backgroundImage = `url("${btn.dataset.skFull}")`;
          btn.appendChild(lens);
        }
        Object.assign(lens.style, { left: img.offsetLeft + 'px', top: img.offsetTop + 'px', width: img.clientWidth + 'px', height: img.clientHeight + 'px', backgroundSize: `${img.clientWidth * factor()}px auto` });
        btn.classList.add('can-magnify');
        lens.classList.add('is-on');
      });
      btn.addEventListener('pointermove', (e) => {
        if (!lens || !lens.classList.contains('is-on')) return;
        const r = img.getBoundingClientRect();
        const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
        const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
        lens.style.backgroundPosition = `${x * 100}% ${y * 100}%`;
      });
      btn.addEventListener('pointerleave', () => { if (lens) lens.classList.remove('is-on'); btn.classList.remove('can-magnify'); });
    });
  }

  /* ---------- Buy-button nudge: a few gentle plays while visible and untouched ---------- */
  function initAttention(scope) {
    if (reduceMotion.matches || !('IntersectionObserver' in window)) return;
    $$('[data-sk-attention]', scope).forEach((btn) => {
      if (btn.dataset.skAttnReady) return;
      btn.dataset.skAttnReady = '1';
      const cls = btn.dataset.skAttention === 'pulse' ? 'sk-attn-pulse' : 'sk-attn-shake';
      const box = btn.closest('form') || btn;
      let plays = 0, timer = 0, visible = false, stopped = false;
      const stop = () => { stopped = true; clearTimeout(timer); btn.classList.remove(cls); };
      ['pointerdown', 'keydown', 'focusin'].forEach((t) => box.addEventListener(t, stop, { once: true }));
      const play = () => {
        if (stopped || !visible || btn.disabled || plays >= 3) return;
        plays += 1;
        btn.classList.remove(cls);
        void btn.offsetWidth;
        btn.classList.add(cls);
        timer = setTimeout(play, 7000);
      };
      btn.addEventListener('animationend', () => btn.classList.remove(cls));
      new IntersectionObserver(([e]) => {
        visible = e.isIntersecting;
        clearTimeout(timer);
        if (visible && !stopped) timer = setTimeout(play, plays ? 4000 : 3000);
      }, { threshold: 0.9 }).observe(btn);
    });
  }

  /* ---------- Quick view (cards [data-sk-quick] → sections/sakal-quick-view.liquid in one <dialog>) ---------- */
  const QuickView = {
    dlg: null, body: null, trigger: null, ctrl: null, cache: new Map(),
    isOpen() { return !!(this.dlg && this.dlg.open); },
    ensure() {
      if (this.dlg) return;
      const d = document.createElement('dialog');
      d.id = 'SkQuickView';
      d.className = 'sk sk-qv-dialog';
      d.setAttribute('aria-labelledby', 'SkQvLabel');
      d.innerHTML = '<div class="sk-qv-dialog__head"><p class="sk-qv-dialog__label" id="SkQvLabel">Quick view</p>'
        + '<button type="button" class="sk-icon-btn" data-sk-qv-close aria-label="Close quick view"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18M6 6l12 12"/></svg></button></div>'
        + '<div class="sk-qv-dialog__body" data-sk-qv-body></div>';
      document.body.appendChild(d);
      this.dlg = d;
      this.body = $('[data-sk-qv-body]', d);
      $('[data-sk-qv-close]', d).addEventListener('click', () => this.close());
      d.addEventListener('click', (e) => { if (e.target === d) this.close(); });
      // Runs once per close, on whichever arrives first: `toggle` → closed, `close`, or our own close()
      this.settle = () => {
        if (this.settled || d.open) return;
        this.settled = true;
        if (this.ctrl) this.ctrl.abort();
        $$('video', d).forEach((v) => v.pause());
        $$('iframe', d).forEach((f) => f.remove());
        if (!document.querySelector('dialog[open]')) document.body.classList.remove('sk-lock');
        // Back to the control that opened it (a drawer opened next takes focus itself)
        if (this.trigger && document.contains(this.trigger) && !Motion.modalOpen()) this.trigger.focus({ preventScroll: true });
      };
      d.addEventListener('toggle', (e) => { if (e.newState === 'closed') this.settle(); });
      d.addEventListener('close', () => this.settle());
    },
    async open(href, trigger) {
      this.ensure();
      this.trigger = trigger;
      const { dlg, body } = this;
      body.setAttribute('aria-busy', 'true');
      body.innerHTML = '<div class="sk-qv-skel" aria-hidden="true"><span class="sk-qv-skel__block"></span><span class="sk-qv-skel__block"></span></div><p class="sk-visually-hidden" role="status">Loading book details</p>';
      dlg.setAttribute('aria-labelledby', 'SkQvLabel');
      if (!dlg.open) { this.settled = false; dlg.showModal(); document.body.classList.add('sk-lock'); }
      if (this.ctrl) this.ctrl.abort();
      const ctrl = (this.ctrl = new AbortController());
      // Brand mark over the skeleton for slower loads (the skeleton is replaced with the book either way)
      const endLoader = Loader.inline($('.sk-qv-skel', body));
      ctrl.signal.addEventListener('abort', endLoader);
      const u = new URL(href, location.origin);
      u.searchParams.set('section_id', 'sakal-quick-view');
      try {
        // Short-lived cache: reopening is instant, but price and stock are never more than a minute old
        const hit = this.cache.get(u.href);
        let html = hit && Date.now() - hit.at < 60000 ? hit.html : '';
        if (!html) {
          const res = await fetchT(u, { signal: ctrl.signal });
          if (!res.ok) throw new Error(String(res.status));
          html = await res.text();
          this.cache.set(u.href, { html, at: Date.now() });
        }
        endLoader();
        if (ctrl !== this.ctrl || !dlg.open) return; // superseded or closed meanwhile
        const root = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sk-qv-root]');
        if (!root) throw new Error('empty');
        body.replaceChildren(document.importNode(root, true));
        body.removeAttribute('aria-busy');
        const title = $('#SkQvTitle', body);
        if (title) { dlg.setAttribute('aria-labelledby', 'SkQvTitle'); title.tabIndex = -1; title.focus({ preventScroll: true }); }
        initProduct(body);
        Wishlist.sync(body);
      } catch (err) {
        endLoader();
        if (err.name === 'AbortError') return;
        body.removeAttribute('aria-busy');
        const box = document.createElement('div');
        box.className = 'sk-qv-error';
        box.setAttribute('role', 'alert');
        const p = document.createElement('p');
        p.textContent = 'Sorry, this book could not be loaded here.';
        const a = document.createElement('a');
        a.className = 'sk-btn sk-btn--ink';
        a.href = href;
        a.textContent = 'Open the book page';
        box.append(p, a);
        body.replaceChildren(box);
      }
    },
    close() { if (this.isOpen()) { this.dlg.close(); this.settle(); } },
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-sk-quick]');
    if (!b) return;
    e.preventDefault();
    QuickView.open(b.dataset.skQuick, b);
  });

  /* ---------- Wishlist page (sections/sakal-wishlist.liquid) ---------- */
  // Each saved book is rendered by the real card component: /products/<handle>?section_id=sakal-card-fragment
  // (Section Rendering API), so price, stock, formats and Buy now come from Shopify at view time, never from storage.
  // Cards already shown are kept on re-render; only new entries are fetched (4 at a time).
  function initWishlistPage(scope = document) {
    $$('[data-sk-wishlist]', scope).forEach((page) => {
      if (page.dataset.skReady) return;
      page.dataset.skReady = '1';
      const grid = $('[data-sk-wl-grid]', page);
      const count = $('[data-sk-wl-count]', page);
      const empty = $('[data-sk-wl-empty]', page);
      const failed = $('[data-sk-wl-error]', page);
      const heading = $('[data-sk-wl-heading]', page);
      if (!grid) return;
      $$('[data-sk-wish-link]').forEach((a) => a.setAttribute('aria-current', 'page'));
      const nodes = new Map(); // product id → rendered <li>
      let ctrl = null;
      let run = 0;
      const say = (n) => { if (count) count.textContent = n ? `${n} saved ${n === 1 ? 'book' : 'books'}` : 'No saved books yet'; };
      const li = (id, cls) => {
        const el = document.createElement('li');
        el.className = 'sk-wl__item' + (cls ? ' ' + cls : '');
        el.dataset.id = id;
        return el;
      };
      const removeBtn = (id, label) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'sk-wl__remove';
        b.dataset.skWlRemove = id;
        b.setAttribute('aria-label', label);
        b.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18M6 6l12 12"/></svg><span aria-hidden="true">Remove</span>';
        return b;
      };
      const skeleton = (id) => {
        const el = li(id, 'is-loading');
        el.setAttribute('aria-hidden', 'true');
        el.innerHTML = '<div class="sk-wl__skel"><span></span><span></span><span></span></div>';
        return el;
      };
      const gone = (id) => {
        const el = li(id, 'is-gone');
        const box = document.createElement('div');
        box.className = 'sk-wl__gone';
        box.innerHTML = '<p class="sk-wl__gone-title">This book is no longer available</p><p class="sk-meta">It may have been removed from the store.</p>';
        el.append(box, removeBtn(id, 'Remove unavailable book from wishlist'));
        return el;
      };
      const retryTile = (id) => {
        const el = li(id, 'is-error');
        const box = document.createElement('div');
        box.className = 'sk-wl__gone';
        box.innerHTML = '<p class="sk-wl__gone-title">Couldn’t load this book</p>';
        const again = document.createElement('button');
        again.type = 'button';
        again.className = 'sk-btn sk-btn--ghost sk-btn--sm';
        again.dataset.skWlRetry = '';
        again.textContent = 'Try again';
        box.appendChild(again);
        el.appendChild(box);
        return el;
      };
      const fetchCard = async (entry, signal) => {
        const res = await fetchT(`${url('products/' + encodeURIComponent(entry.handle))}?section_id=sakal-card-fragment`, { signal });
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(String(res.status));
        const frag = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('[data-sk-card-fragment]');
        const card = frag && frag.querySelector('.sk-card');
        // A different product at this handle (handle reused) counts as gone
        if (!card || Number(frag.dataset.productId) !== entry.id) return null;
        if (frag.dataset.handle) Wishlist.rehandle(entry.id, frag.dataset.handle);
        const el = li(entry.id);
        const title = ($('.sk-card__title', card)?.textContent || 'this book').trim();
        el.append(document.importNode(card, true), removeBtn(entry.id, `Remove ${title} from wishlist`));
        return el;
      };
      const render = async () => {
        const list = Wishlist.list();
        const mine = ++run;
        if (ctrl) ctrl.abort();
        ctrl = new AbortController();
        const { signal } = ctrl;
        say(list.length);
        if (failed) failed.hidden = true;
        if (empty) empty.hidden = list.length > 0;
        grid.hidden = list.length === 0;
        for (const [id, el] of nodes) if (!Wishlist.has(id)) { el.remove(); nodes.delete(id); }
        if (!list.length) { grid.replaceChildren(); return; }
        const todo = list.filter((e) => !nodes.has(e.id));
        const slots = new Map(todo.map((e) => [e.id, skeleton(e.id)]));
        grid.replaceChildren(...list.map((e) => nodes.get(e.id) || slots.get(e.id)));
        if (!todo.length) return;
        grid.setAttribute('aria-busy', 'true');
        let errors = 0;
        const queue = [...todo];
        const worker = async () => {
          for (let e = queue.shift(); e; e = queue.shift()) {
            let el;
            try { el = (await fetchCard(e, signal)) || gone(e.id); } catch (err) {
              if (err.name === 'AbortError') return;
              errors++;
              el = retryTile(e.id);
            }
            if (mine !== run) return;
            slots.get(e.id).replaceWith(el);
            if (!el.classList.contains('is-error')) nodes.set(e.id, el);
          }
        };
        await Promise.all([worker(), worker(), worker(), worker()]);
        if (mine !== run) return;
        grid.removeAttribute('aria-busy');
        Wishlist.sync(grid);
        // Nothing could be loaded at all (offline, store unreachable): one clear message instead of a wall of tiles
        if (errors && errors === todo.length && !nodes.size && failed) { grid.hidden = true; failed.hidden = false; }
      };
      // Removing a book: fade its tile out, keep keyboard focus on the grid (next book's title, else the heading)
      const dropTile = (id) => {
        const el = nodes.get(id) || $(`.sk-wl__item[data-id="${id}"]`, grid);
        nodes.delete(id);
        if (!el) return render();
        const hadFocus = el.contains(document.activeElement);
        const next = el.nextElementSibling || el.previousElementSibling;
        const done = () => {
          el.remove();
          say(Wishlist.items.length);
          if (!Wishlist.items.length) { grid.hidden = true; if (empty) empty.hidden = false; }
          if (hadFocus) {
            const f = next && next.isConnected && $('.sk-card__title, button, a', next);
            if (f) f.focus(); else if (heading) heading.focus();
          }
        };
        if (reduceMotion.matches || typeof el.animate !== 'function') return done();
        el.style.pointerEvents = 'none';
        el.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(.96)' }], { duration: 220, easing: 'ease-out', fill: 'forwards' }).finished.then(done, done);
      };
      page.addEventListener('click', (e) => {
        const r = e.target.closest('[data-sk-wl-remove]');
        if (r) {
          if (Wishlist.remove(r.dataset.skWlRemove)) toast('Removed from wishlist');
          return;
        }
        if (e.target.closest('[data-sk-wl-retry]')) render();
      });
      // One document listener per page instance, released when the Theme Editor replaces the section
      const ac = new AbortController();
      document.addEventListener('sakal:wishlist:change', (e) => {
        const d = e.detail || {};
        if (d.type === 'remove') dropTile(d.id);
        else render(); // added here or changed in another tab
      }, { signal: ac.signal });
      document.addEventListener('shopify:section:unload', (e) => { if (e.target.contains(page)) { ac.abort(); if (ctrl) ctrl.abort(); } }, { signal: ac.signal });
      render();
    });
  }

  /* ---------- Collection: filter drawer + auto-submit ---------- */
  function initFacets(scope = document) {
    $$('form[data-sk-facets]', scope).forEach((form) => {
      if (form.dataset.skReady) return;
      form.dataset.skReady = '1';
      form.addEventListener('change', (e) => {
        if (e.target.matches('[data-sk-instant]') || window.matchMedia('(min-width: 1100px)').matches) {
          if (e.target.type === 'number') return;
          form.requestSubmit ? form.requestSubmit() : form.submit();
        }
      });
      // Strip empty price fields so URLs stay clean
      form.addEventListener('submit', () => {
        $$('input[type=number]', form).forEach((i) => { if (!i.value) i.disabled = true; });
      });
    });
    $$('select[data-sk-sort]', scope).forEach((s) => {
      if (s.dataset.skReady) return;
      s.dataset.skReady = '1';
      s.addEventListener('change', () => {
        const u = new URL(location.href);
        u.searchParams.set('sort_by', s.value);
        u.searchParams.delete('page');
        navStart(); // browsers without the Navigation API (and no beforeunload on iOS) don't report scripted loads
        location.href = u.toString();
      });
    });
  }

  /* ---------- Sub-shelf chips (filter current page by product category) ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-sk-subcat]');
    if (!b) return;
    const row = b.closest('[data-sk-subcats]');
    const cat = b.dataset.skSubcat;
    $$('[data-sk-subcat]', row).forEach((x) => { const on = x === b; x.classList.toggle('is-active', on); x.setAttribute('aria-pressed', String(on)); });
    let shown = 0;
    $$('.sk-plp .sk-grid > li').forEach((li) => {
      const cats = (li.querySelector('.sk-card')?.dataset.cats || '').split(' ');
      const on = !cat || cats.includes(cat);
      li.hidden = !on;
      if (on) shown++;
    });
    const live = row.parentElement.querySelector('[data-sk-subcat-live]');
    if (live) live.textContent = `${shown} ${shown === 1 ? 'book' : 'books'} shown`;
  });

  /* ---------- Product recommendations (lazy) ---------- */
  function initRecs(scope = document) {
    $$('[data-sk-recs]', scope).forEach((sec) => {
      if (sec.dataset.skReady || sec.querySelector('[data-sk-recs-inner]')) return;
      sec.dataset.skReady = '1';
      const load = async () => {
        try {
          const res = await fetch(sec.dataset.skRecs);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const inner = doc.querySelector('[data-sk-recs-inner]');
          if (inner) { sec.appendChild(inner); initRails(sec); document.dispatchEvent(new CustomEvent('sakal:content:changed')); } else sec.hidden = true;
        } catch (_) { sec.hidden = true; }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '600px 0px' });
        io.observe(sec);
      } else load();
    });
  }

  /* ---------- Frequently bought together (sections/sakal-fbt.liquid) ---------- */
  // The shell holds this book; suggestions come from the recommendations endpoint (complementary, then related).
  // Checked books total up from their own data-price / data-was, and one button adds them all in a single request.
  function initFbt(scope = document) {
    $$('[data-sk-fbt]', scope).forEach((sec) => {
      if (sec.dataset.skReady) return;
      sec.dataset.skReady = '1';
      const list = $('[data-sk-fbt-list]', sec);
      if (!list) return;
      const inr = (cents) => {
        try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100); }
        catch (_) { return '₹' + (cents / 100).toFixed(2); }
      };
      const items = () => $$('[data-sk-fbt-item]', list);
      const update = (announce) => {
        const on = items().filter((li) => $('[data-sk-fbt-check]', li).checked);
        items().forEach((li) => li.classList.toggle('is-off', !$('[data-sk-fbt-check]', li).checked));
        const now = on.reduce((s, li) => s + Number(li.dataset.price), 0);
        const was = on.reduce((s, li) => s + Number(li.dataset.was), 0);
        const n = on.length;
        $$('[data-sk-fbt-count]', sec).forEach((el) => (el.textContent = n));
        $$('[data-sk-fbt-noun]', sec).forEach((el) => (el.textContent = n === 1 ? 'book' : 'books'));
        $('[data-sk-fbt-total]', sec).textContent = inr(now);
        const wasEl = $('[data-sk-fbt-was]', sec), saveEl = $('[data-sk-fbt-save]', sec);
        wasEl.hidden = !(was > now); wasEl.textContent = was > now ? inr(was) : '';
        saveEl.hidden = !(was > now); saveEl.textContent = was > now ? `You save ${inr(was - now)}` : '';
        const btn = $('[data-sk-fbt-add]', sec);
        btn.disabled = n === 0;
        $('[data-sk-fbt-add-label]', sec).textContent = n > 1 ? `Add all ${n} to bag` : n === 1 ? 'Add to bag' : 'Choose a book';
        if (announce) $('[data-sk-fbt-live]', sec).textContent = `${n} ${n === 1 ? 'book' : 'books'} selected, total ${inr(now)}`;
      };
      const fetchRecs = async (url) => {
        const res = await fetch(url);
        if (!res.ok) return [];
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        return $$('[data-sk-fbt-recs] [data-sk-fbt-item]', doc).filter((li) => li.dataset.productId !== sec.dataset.skFbtSelf);
      };
      const load = async () => {
        try {
          let recs = await fetchRecs(sec.dataset.skFbt);
          if (!recs.length && sec.dataset.skFbtFallback) recs = await fetchRecs(sec.dataset.skFbtFallback);
          recs = recs.slice(0, Number(sec.dataset.skFbtLimit) || 2);
          if (!recs.length) { sec.hidden = true; return; }
          recs.forEach((li) => list.appendChild(document.importNode(li, true)));
          update(false);
          sec.classList.remove('is-pending');
          document.dispatchEvent(new CustomEvent('sakal:content:changed'));
        } catch (_) { sec.hidden = true; }
      };
      list.addEventListener('change', (e) => { if (e.target.matches('[data-sk-fbt-check]')) update(true); });
      $('[data-sk-fbt-add]', sec).addEventListener('click', (e) => {
        // This book follows the format chosen in the purchase box above, when there is one
        const pageId = $('[data-sk-product] form[data-sk-product-form] input[name=id]');
        const picked = items().filter((li) => $('[data-sk-fbt-check]', li).checked).map((li) => ({
          id: Number(li.classList.contains('is-this') && pageId ? pageId.value : li.dataset.id),
          quantity: 1
        }));
        if (picked.length) Cart.add(picked, e.currentTarget);
      });
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(([en]) => { if (en.isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '800px 0px' });
        io.observe(sec);
      } else load();
    });
  }

  /* ---------- Discount tag stamp (Theme settings → Offers) ---------- */
  // Each "% off" tag stamps in once when it scrolls into view. Tags are only pre-hidden after the observers
  // exist (html.sk-stamp-on), and tags added later (rails, tabs, recommendations) are picked up by a
  // MutationObserver, so a tag is never left hidden. Skipped entirely for reduced motion.
  const Stamp = {
    io: null,
    init() {
      if (this.io || !document.body.hasAttribute('data-sk-stamp') || reduceMotion.matches) return;
      if (!('IntersectionObserver' in window) || !('MutationObserver' in window)) return;
      this.io = new IntersectionObserver((entries) => {
        entries.filter((e) => e.isIntersecting).forEach((e, i) => {
          this.io.unobserve(e.target);
          setTimeout(() => e.target.classList.add('is-stamped'), i * 70);
        });
      }, { rootMargin: '0px 0px -6% 0px' });
      this.scan(document);
      new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach((n) => { if (n.nodeType === 1) this.scan(n); })))
        .observe(document.body, { childList: true, subtree: true });
      document.documentElement.classList.add('sk-stamp-on');
    },
    scan(root) {
      const tags = root.matches && root.matches('.sk-badge--sale') ? [root] : $$('.sk-badge--sale', root);
      tags.forEach((t) => { if (!t.classList.contains('is-stamped')) this.io.observe(t); });
    }
  };

  /* ---------- Footer columns: open on >=600px, accordion below ---------- */
  function initFooter(scope = document) {
    const mq = window.matchMedia('(min-width: 600px)');
    const sync = () => $$('[data-sk-fcol]', document).forEach((d) => (d.open = mq.matches));
    sync();
    if (!initFooter.bound) { mq.addEventListener('change', sync); initFooter.bound = true; }
  }

  /* ---------- Book reels (sections/sakal-book-reels.liquid) ---------- */
  // Cards carry posters only. A reel's <video> (or YouTube iframe) is created when it opens in the section's
  // <dialog> and torn down on switch/close, so one video plays at a time and nothing downloads up front.
  // Looping (carousel/marquee with auto-scroll; focus with 3+ reels): the card set is cloned — aria-hidden,
  // untabbable, no editor attributes — before and after the originals, and scrollLeft wraps by exactly one set
  // width, so the loop has no seam and native touch/trackpad scrolling keeps working. Auto-movement holds while
  // hovered, keyboard-focused, touched, off screen, in a hidden tab or while the viewer is open; reduced motion →
  // no automatic movement (scrolling and arrows remain). Add to bag goes through Cart.add; the viewer keeps its own
  // status line because the toast sits beneath a modal dialog.
  function initReels(scope = document) {
    $$('[data-sk-reels]', scope).forEach((sec) => {
      if (sec.dataset.skReady) return;
      sec.dataset.skReady = '1';
      const track = $('[data-sk-reels-track]', sec);
      if (!track) return;
      const ac = new AbortController();
      const opt = { signal: ac.signal };
      const html = document.documentElement;
      const focusMode = sec.dataset.layout === 'focus';
      const originals = $$('[data-sk-reel-item]', track);
      const count = originals.length;
      const dlg = $('[data-sk-reel-viewer]', sec);
      const prev = $('[data-sk-prev]', sec);
      const next = $('[data-sk-next]', sec);
      const toggle = $('[data-sk-reels-toggle]', sec);
      const bar = $('[data-sk-reels-bar]', sec);
      const dots = $$('[data-sk-reels-dot]', sec);
      const speed = Number(sec.dataset.speed) || 30;
      const dir = sec.dataset.direction === 'right' ? -1 : 1;
      const autoOn = () => sec.dataset.auto === 'true' && !reduceMotion.matches && count > 1;
      const smooth = () => (reduceMotion.matches ? 'auto' : 'smooth');

      // --- Geometry (measured on build and width changes only; scroll handlers read cached values) ---
      let items = originals, looping = false, lo = 0, setW = 0, padL = 0, gap = 0, cw = 0, lastW = -1, centers = [];
      const originX = () => items[0].offsetLeft - padL;
      const leftOf = (el) => el.offsetLeft - originX() - padL;
      const centreOf = (el) => el.offsetLeft - originX() + el.offsetWidth / 2 - cw / 2;
      const wrap = (x) => (!looping ? x : x < lo ? x + setW : x >= lo + setW ? x - setW : x);
      const cloneSet = () => originals.map((li) => {
        const c = li.cloneNode(true);
        c.removeAttribute('data-sk-reel-item');
        c.setAttribute('data-sk-reel-clone', '');
        c.setAttribute('aria-hidden', 'true');
        $$('[data-shopify-editor-block]', c).forEach((el) => el.removeAttribute('data-shopify-editor-block'));
        $$('a, button, input, select', c).forEach((el) => el.setAttribute('tabindex', '-1'));
        $$('[id]', c).forEach((el) => el.removeAttribute('id'));
        return c;
      });
      const build = () => {
        $$('[data-sk-reel-clone]', track).forEach((c) => c.remove());
        looping = false;
        sec.classList.remove('is-looping');
        items = originals;
        cw = track.clientWidth;
        lastW = Math.round(cw);
        if (count > 1 && (focusMode ? count >= 3 : autoOn())) {
          sec.classList.add('is-looping'); // measure in the looping layout (focus drops its centring padding)
          gap = parseFloat(getComputedStyle(track).columnGap) || 0;
          const first = originals[0], last = originals[count - 1];
          const contentW = last.offsetLeft + last.offsetWidth - first.offsetLeft + gap;
          if (focusMode || contentW > cw + 1) { // a set that already fits on screen has nothing to loop
            const before = Math.max(1, Math.ceil((1.5 * cw) / contentW));
            const after = Math.max(1, Math.ceil((2.5 * cw) / contentW));
            const frag = document.createDocumentFragment();
            for (let k = 0; k < before; k++) frag.append(...cloneSet());
            track.prepend(frag);
            for (let k = 0; k < after; k++) track.append(...cloneSet());
            looping = true;
            items = $$(':scope > li', track);
            padL = parseFloat(getComputedStyle(track).paddingLeft) || 0;
            setW = items[(before + 1) * count].offsetLeft - first.offsetLeft;
            lo = focusMode ? centreOf(first) - (first.offsetWidth + gap) / 2 : leftOf(first);
          } else sec.classList.remove('is-looping');
        }
        gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        padL = parseFloat(getComputedStyle(track).paddingLeft) || 0;
        if (focusMode) centers = items.map((el) => el.offsetLeft - originX() + el.offsetWidth / 2);
        track.scrollLeft = looping ? (focusMode ? centreOf(originals[0]) : lo) : 0;
        pos = wrote = track.scrollLeft; // our own write: the scroll event it fires is not a user scroll
        if (toggle) toggle.hidden = !(autoOn() && (looping || focusMode));
        paint();
      };
      const normalize = () => {
        if (!looping) return;
        const x = track.scrollLeft, y = wrap(x);
        if (y !== x) track.scrollLeft = y;
        pos = wrote = track.scrollLeft;
        paint();
      };

      // --- Active card (focus), progress, arrows ---
      let activeEl = null;
      const paint = () => {
        const sl = track.scrollLeft;
        if (focusMode && items.length) {
          const mid = sl + cw / 2;
          let best = 0, dist = Infinity;
          centers.forEach((c, k) => { const d = Math.abs(c - mid); if (d < dist) { dist = d; best = k; } });
          const el = items[best];
          if (el && el !== activeEl) {
            if (activeEl) activeEl.classList.remove('is-active');
            el.classList.add('is-active');
            activeEl = el;
            const p = Number(el.dataset.pos);
            dots.forEach((d, k) => d.classList.toggle('is-active', k === p));
          }
        }
        if (bar) {
          if (looping) {
            const vis = Math.min(1, cw / setW);
            const f = ((((sl - lo) % setW) + setW) % setW) / setW;
            bar.style.width = vis * 100 + '%';
            bar.style.transform = `translateX(${(f / vis) * 100}%)`;
          } else {
            const sw = track.scrollWidth, max = sw - cw, vis = sw ? Math.min(1, cw / sw) : 1;
            bar.style.width = vis * 100 + '%';
            bar.style.transform = `translateX(${max > 0 ? (sl / max) * ((1 - vis) / vis) * 100 : 0}%)`;
          }
        }
        if (prev) prev.disabled = !looping && sl <= 4;
        if (next) next.disabled = !looping && sl >= track.scrollWidth - cw - 4;
      };

      // --- Auto movement: one rAF loop (carousel/marquee) or one interval (focus) per section ---
      const holds = new Set();
      const timers = {};
      let raf = 0, tick = 0, last = 0, pos = 0, wrote = -1, userPaused = false, onScreen = true;
      const canRun = () => autoOn() && !userPaused && !holds.size && onScreen && !document.hidden && !(dlg && dlg.open);
      const frame = (t) => {
        raf = requestAnimationFrame(frame);
        if (!last) { last = t; pos = track.scrollLeft; return; }
        const dt = Math.min(50, t - last);
        last = t;
        pos = wrap(pos + (dir * speed * dt) / 1000);
        track.scrollLeft = pos;
        wrote = track.scrollLeft;
        paint();
      };
      const sync = () => {
        const run = canRun() && (looping || focusMode);
        if (focusMode) {
          if (run && !tick) tick = setInterval(() => go(1), 5000);
          else if (!run && tick) { clearInterval(tick); tick = 0; }
        } else if (run && !raf) { last = 0; raf = requestAnimationFrame(frame); }
        else if (!run && raf) { cancelAnimationFrame(raf); raf = 0; }
      };
      const hold = (r) => { clearTimeout(timers[r]); holds.add(r); sync(); };
      const release = (r) => { clearTimeout(timers[r]); holds.delete(r); sync(); };
      const holdFor = (r, ms) => { hold(r); timers[r] = setTimeout(() => release(r), ms); };

      // Focus layout: step one card from the current centre (clones are contiguous, so siblings always exist)
      const go = (d) => {
        normalize();
        const cur = activeEl || originals[0];
        let el = d > 0 ? cur.nextElementSibling : cur.previousElementSibling;
        if (!el) el = d > 0 ? items[0] : items[items.length - 1]; // two-reel focus (no loop): wrap around
        track.scrollTo({ left: centreOf(el), behavior: smooth() });
      };
      const page = (d) => {
        if (focusMode) { go(d); if (tick) { clearInterval(tick); tick = 0; sync(); } return; }
        holdFor('nav', 1400); // the smooth scroll must finish before auto-movement resumes
        normalize();
        const w = originals[0].offsetWidth + gap;
        track.scrollBy({ left: d * Math.max(1, Math.floor((cw + gap) / w)) * w, behavior: smooth() });
      };
      prev?.addEventListener('click', () => page(-1), opt);
      next?.addEventListener('click', () => page(1), opt);
      toggle?.addEventListener('click', () => {
        userPaused = !userPaused;
        toggle.setAttribute('aria-pressed', String(userPaused));
        toggle.setAttribute('aria-label', userPaused ? 'Play moving reels' : 'Pause moving reels');
        sync();
      }, opt);

      let scrollFrame = 0, idleT = 0;
      track.addEventListener('scroll', () => {
        if (raf && Math.abs(track.scrollLeft - wrote) > 2) holdFor('touch', 2500); // trackpad/keyboard scroll
        if (!scrollFrame) scrollFrame = requestAnimationFrame(() => { scrollFrame = 0; paint(); });
        clearTimeout(idleT);
        // Once scrolling settles, hop back into the middle copy (identical cards, so the hop is invisible)
        idleT = setTimeout(() => { if (!raf && !track.contains(document.activeElement)) normalize(); }, 160);
      }, { passive: true, ...opt });
      track.addEventListener('pointerdown', () => hold('touch'), opt);
      ['pointerup', 'pointercancel', 'touchend'].forEach((ev) => track.addEventListener(ev, () => holdFor('touch', 2500), { passive: true, ...opt }));
      track.addEventListener('touchstart', () => hold('touch'), { passive: true, ...opt });
      track.addEventListener('wheel', () => holdFor('touch', 2500), { passive: true, ...opt });
      if (sec.dataset.hoverPause === 'true') {
        track.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hold('hover'); }, opt);
        track.addEventListener('pointerleave', () => release('hover'), opt);
      }
      sec.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) hold('focus'); }, opt);
      sec.addEventListener('focusout', (e) => { if (!sec.contains(e.relatedTarget)) release('focus'); }, opt);
      document.addEventListener('visibilitychange', sync, opt);
      reduceMotion.addEventListener('change', () => { build(); sync(); }, opt);
      const io = new IntersectionObserver(([en]) => { onScreen = en.isIntersecting; sync(); });
      io.observe(sec);
      const ro = new ResizeObserver(() => { if (Math.round(track.clientWidth) !== lastW) { build(); sync(); } });
      ro.observe(track);
      // Theme Editor: selecting a reel block stops the motion and brings that card into view
      document.addEventListener('shopify:block:select', (e) => {
        if (!sec.contains(e.target)) return;
        hold('editor');
        const li = e.target.closest('[data-sk-reel-item]');
        if (li) { normalize(); track.scrollTo({ left: focusMode ? centreOf(li) : leftOf(li), behavior: smooth() }); }
      }, opt);
      document.addEventListener('shopify:block:deselect', (e) => { if (sec.contains(e.target)) release('editor'); }, opt);

      // --- Card hover preview: smallest rendition, muted, only while a fine pointer is over the card ---
      let previewEl = null;
      const stopPreview = () => {
        if (!previewEl) return;
        const media = previewEl.parentElement;
        previewEl.pause();
        previewEl.removeAttribute('src');
        previewEl.load();
        previewEl.remove();
        if (media) media.classList.remove('is-previewing');
        previewEl = null;
      };
      const saveData = () => !!(navigator.connection && navigator.connection.saveData);
      // Shopify videos come as one MP4 plus an HLS master playlist whose #EXT-X-STREAM-INF renditions (1080p, 720p,
      // 480p…) become the quality choices and the light hover-preview source — where the browser plays HLS natively.
      let hlsOk = null;
      const canHls = () => (hlsOk === null ? (hlsOk = document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== '') : hlsOk);
      const hlsCache = new Map();
      const hlsVariants = (master) => {
        if (!hlsCache.has(master)) {
          const base = new URL(master, location.href);
          hlsCache.set(master, fetchT(base.href).then((r) => (r.ok ? r.text() : '')).then((txt) => {
            const lines = txt.split(/\r?\n/);
            const out = [];
            lines.forEach((l, k) => {
              const m = l.match(/^#EXT-X-STREAM-INF:.*?RESOLUTION=(\d+)x(\d+)/);
              const uri = (lines[k + 1] || '').trim();
              if (m && uri && !uri.startsWith('#')) out.push({ w: +m[1], h: +m[2], u: new URL(uri, base).href, hls: true });
            });
            return out.sort((a, b) => a.h - b.h);
          }).catch(() => []));
        }
        return hlsCache.get(master);
      };
      if (sec.dataset.preview === 'hover') {
        track.addEventListener('pointerover', (e) => {
          if (e.pointerType !== 'mouse' || !finePointer.matches || reduceMotion.matches || saveData() || (dlg && dlg.open)) return;
          const media = e.target.closest('.sk-reelcard__media[data-preview], .sk-reelcard__media[data-preview-hls]');
          if (!media || (previewEl && previewEl.parentElement === media)) return;
          if (!media.dataset.preview && !canHls()) return; // only a full-size MP4 here: no preview rather than a heavy one
          stopPreview();
          const v = document.createElement('video');
          v.className = 'sk-reelcard__preview';
          v.muted = true;
          v.loop = true;
          v.playsInline = true;
          v.preload = 'auto';
          v.setAttribute('aria-hidden', 'true');
          v.addEventListener('playing', () => { if (previewEl === v) media.classList.add('is-previewing'); }, { once: true });
          media.prepend(v);
          previewEl = v;
          if (media.dataset.preview) { v.src = media.dataset.preview; v.play().catch(() => {}); }
          else {
            hlsVariants(media.dataset.previewHls).then((vs) => {
              if (previewEl !== v || !vs.length) return;
              v.src = vs[0].u;
              v.play().catch(() => {});
            });
          }
        }, opt);
        track.addEventListener('pointerout', (e) => {
          if (!previewEl) return;
          const media = previewEl.parentElement;
          if (media && !media.contains(e.relatedTarget)) stopPreview();
        }, opt);
      }

      // --- Viewer ---
      let unload = () => {};
      if (dlg) {
        const tpls = $$('template[data-sk-reel]', dlg);
        const inner = $('[data-sk-reel-inner]', dlg);
        const frame = $('[data-sk-reel-frame]', dlg);
        const player = $('[data-sk-reel-player]', dlg);
        const stage = $('[data-sk-reel-stage]', dlg);
        const hud = $('[data-sk-reel-hud]', dlg);
        const pillSlot = $('[data-sk-reel-pill]', dlg);
        const panel = $('[data-sk-reel-panel]', dlg);
        const panelBody = $('[data-sk-reel-panelbody]', dlg);
        const bg = $('[data-sk-reel-bg]', dlg);
        const status = $('[data-sk-reel-status]', dlg);
        const counter = $('[data-sk-reel-count]', dlg);
        const fill = $('[data-sk-reel-fill]', dlg);
        const seek = $('[data-sk-reel-seek]', dlg);
        const playBtn = $('[data-sk-reel-play]', dlg);
        const muteBtn = $('[data-sk-reel-mute]', dlg);
        const qWrap = $('[data-sk-reel-quality]', dlg);
        const qBtn = $('[data-sk-reel-qbtn]', dlg);
        const qMenu = $('[data-sk-reel-qmenu]', dlg);
        const qLabel = $('[data-sk-reel-qlabel]', dlg);
        const moreBtns = $$('[data-sk-reel-go]', dlg);
        const phone = window.matchMedia('(max-width: 899px)');
        const autoNext = dlg.dataset.autoadvance === 'true';
        let cur = 0, trigger = null, media = null, sources = [], muted = false, progressRaf = 0, statusT = 0;
        let pref = dlg.dataset.quality || 'auto';
        try { pref = localStorage.getItem('sk-reel-quality') || pref; } catch (_) {}

        const say = (msg, kind) => {
          clearTimeout(statusT);
          status.textContent = msg;
          status.classList.toggle('is-error', kind === 'error');
          status.classList.toggle('is-ok', kind === 'ok');
          if (msg && kind !== 'busy') statusT = setTimeout(() => { status.textContent = ''; }, 3200);
        };
        const lab = (s) => s.h || s.w || 0; // renditions are named by height, as Shopify does (1080p, 720p…)
        const qName = (s) => (lab(s) ? `${lab(s)}p` : 'Original');
        // Quality: Auto = HLS adaptive streaming where the browser plays it natively, otherwise the rendition that
        // covers the player's pixel width (480p on slow / data-saver connections). Presets cap the height; a number
        // is that rendition exactly.
        const pick = (p) => {
          if (!sources.length) return null;
          if (p === 'auto') {
            const c = navigator.connection;
            const slow = c && (c.saveData || /(^|-)(2g|3g)$/.test(c.effectiveType || ''));
            if (slow) return sources.filter((s) => lab(s) <= 480).pop() || sources[0];
            const need = Math.round(player.clientWidth * (window.devicePixelRatio || 1));
            return sources.find((s) => s.w >= need) || sources[sources.length - 1];
          }
          const cap = { high: 1080, standard: 720, saver: 480 }[p] || Number(p);
          const fit = sources.filter((s) => lab(s) <= cap);
          return fit.length ? fit[fit.length - 1] : sources[0];
        };
        const buildQuality = () => {
          const list = [...new Map(sources.filter((s) => lab(s)).map((s) => [lab(s), s])).values()];
          qWrap.hidden = list.length < 2;
          if (qWrap.hidden) return;
          const chosen = pref === 'auto' ? null : pick(pref);
          const named = chosen ? String(lab(chosen)) : 'auto';
          qMenu.replaceChildren(...[{ k: 'auto', t: 'Auto', sub: 'Best for your screen' }, ...list.slice().reverse().map((s) => ({ k: String(lab(s)), t: qName(s), sub: lab(s) >= 720 ? 'HD' : '' }))].map((o) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'sk-reelview__qitem';
            b.setAttribute('role', 'menuitemradio');
            b.setAttribute('aria-checked', String(o.k === named));
            b.dataset.q = o.k;
            b.innerHTML = `<span>${o.t}</span>${o.sub ? `<small>${o.sub}</small>` : ''}`;
            return b;
          }));
        };
        const setQLabel = (s) => { qLabel.textContent = s && lab(s) ? (pref === 'auto' ? `Auto · ${qName(s)}` : qName(s)) : 'Auto'; };
        const closeQ = () => { qMenu.hidden = true; qBtn.setAttribute('aria-expanded', 'false'); };

        const setPaused = (p) => {
          player.classList.toggle('is-paused', p);
          playBtn.setAttribute('aria-label', p ? 'Play' : 'Pause');
        };
        const tickProgress = () => {
          cancelAnimationFrame(progressRaf);
          const step = () => {
            if (media instanceof HTMLVideoElement && media.duration) {
              const f = media.currentTime / media.duration;
              fill.style.transform = `scaleX(${f})`;
              seek.value = String(Math.round(f * 1000));
              seek.setAttribute('aria-valuetext', `${Math.floor(media.currentTime)} of ${Math.floor(media.duration)} seconds`);
            }
            if (media instanceof HTMLVideoElement && !media.paused) progressRaf = requestAnimationFrame(step);
          };
          step();
        };
        unload = () => {
          cancelAnimationFrame(progressRaf);
          if (media instanceof HTMLVideoElement) { media.pause(); media.removeAttribute('src'); media.load(); }
          media = null;
          stage.replaceChildren();
          stage.classList.remove('is-loading');
          player.classList.remove('is-loading', 'is-embed');
          fill.style.transform = 'scaleX(0)';
          seek.value = '0';
        };
        const fail = () => {
          media = null;
          stage.classList.remove('is-loading');
          player.classList.remove('is-loading');
          const p = document.createElement('p');
          p.className = 'sk-reelview__error';
          p.setAttribute('role', 'alert');
          p.textContent = 'This video can’t be played right now.';
          stage.replaceChildren(p);
        };
        let mp4List = [];
        const loadVideo = (v, d, keepTime, noHls) => {
          const native = !noHls && !!d.hls && canHls();
          const hlsKnown = native && sources.length > 0 && !!sources[0].hls;
          v.dataset.hls = native ? '1' : '';
          // Native HLS starts on the adaptive master at once (inside the tap's user activation); its renditions load
          // alongside and a fixed choice swaps in when they arrive, keeping the playback position.
          const s = !native || (hlsKnown && pref !== 'auto') ? pick(pref) : null;
          v.src = s ? s.u : native ? d.hls : d.src;
          setQLabel(s);
          if (keepTime) v.addEventListener('loadedmetadata', () => { v.currentTime = keepTime; }, { once: true });
          if (!native || hlsKnown) return;
          hlsVariants(d.hls).then((vs) => {
            if (media !== v || !vs.length || v.dataset.hls !== '1') return;
            sources = vs;
            buildQuality();
            if (pref === 'auto') return;
            const fixed = pick(pref);
            if (!fixed) return;
            const at = v.currentTime, wasPlaying = !v.paused;
            v.src = fixed.u;
            setQLabel(fixed);
            v.addEventListener('loadedmetadata', () => { v.currentTime = at; }, { once: true });
            if (wasPlaying) playMedia(v);
          });
        };
        const playMedia = (v) => {
          v.muted = muted;
          v.play().catch(() => {
            // No sound allowed without a fresh gesture (e.g. auto-advance): continue muted, say so on the button
            if (!v.muted) { muted = true; muteBtn.setAttribute('aria-pressed', 'true'); muteBtn.setAttribute('aria-label', 'Unmute'); v.muted = true; v.play().catch(() => setPaused(true)); }
            else setPaused(true);
          });
        };
        const show = (i, dir) => {
          cur = (i + tpls.length) % tpls.length;
          stopPreview();
          unload();
          closeQ();
          closeSheet(true);
          const t = tpls[cur];
          const d = t.dataset;
          const part = (name) => { const el = t.content.querySelector(`[data-part="${name}"]`); return el ? [...el.cloneNode(true).childNodes] : []; };
          hud.replaceChildren(...part('hud'));
          pillSlot.replaceChildren(...part('pill'));
          panelBody.replaceChildren(...part('panel'));
          frame.classList.toggle('no-panel', !panelBody.children.length && !moreBtns.length);
          moreBtns.forEach((b) => b.setAttribute('aria-current', String(Number(b.dataset.skReelGo) === cur)));
          // Scroll only the strip itself: scrollIntoView would also scroll the dialog/panel (the sheet is off-screen)
          const now = moreBtns.find((b) => Number(b.dataset.skReelGo) === cur);
          const strip = now && now.closest('ul');
          if (strip) strip.scrollLeft = Math.max(0, now.offsetLeft - strip.offsetLeft - (strip.clientWidth - now.offsetWidth) / 2);
          if (counter) counter.textContent = tpls.length > 1 ? `${cur + 1} / ${tpls.length}` : '';
          if (bg) { if (d.poster) bg.src = d.poster; bg.hidden = !d.poster; }
          if (dir && !reduceMotion.matches) { player.dataset.dir = dir; void player.offsetWidth; } else delete player.dataset.dir;
          player.classList.add('is-loading');
          stage.classList.add('is-loading');
          setPaused(false);
          if (d.kind === 'youtube') {
            const id = (d.src.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{11})/) || [])[1];
            if (!id) return fail();
            player.classList.add('is-embed');
            const f = document.createElement('iframe');
            f.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`;
            f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
            f.allowFullscreen = true;
            f.title = d.title;
            f.addEventListener('load', () => { stage.classList.remove('is-loading'); player.classList.remove('is-loading'); }, { once: true });
            stage.replaceChildren(f);
            media = f;
            return;
          }
          try { sources = JSON.parse(d.sources || '[]').filter((s) => s.u).sort((a, b) => lab(a) - lab(b)); } catch (_) { sources = []; }
          mp4List = sources;
          buildQuality();
          const v = document.createElement('video');
          v.playsInline = true;
          v.preload = 'auto';
          if (d.poster) v.poster = d.poster;
          v.setAttribute('aria-label', d.title);
          v.addEventListener('loadeddata', () => { stage.classList.remove('is-loading'); player.classList.remove('is-loading'); player.classList.toggle('is-contain', v.videoWidth > v.videoHeight); }, { once: true });
          // A stream the browser can't play (e.g. HLS refused) falls back to the MP4 renditions before giving up
          v.addEventListener('error', () => {
            if (media !== v) return;
            if (v.dataset.hls === '1') { sources = mp4List; buildQuality(); loadVideo(v, d, v.currentTime, true); playMedia(v); } else fail();
          });
          v.addEventListener('play', () => { setPaused(false); tickProgress(); });
          v.addEventListener('pause', () => { setPaused(true); tickProgress(); });
          v.addEventListener('seeked', tickProgress);
          v.addEventListener('ended', () => {
            if (autoNext && tpls.length > 1 && !sheetOpen && qMenu.hidden && !dlg.contains(document.activeElement?.closest('.sk-reelbook'))) show(cur + 1, 'up');
            else setPaused(true);
          });
          loadVideo(v, d);
          stage.replaceChildren(v);
          media = v;
          $$('video').forEach((o) => o !== v && !o.paused && o.pause()); // never two at once
          playMedia(v);
        };
        const togglePlay = () => {
          if (!(media instanceof HTMLVideoElement)) return;
          if (media.paused) playMedia(media); else media.pause();
        };

        // Bottom sheet (phones). On desktop the panel is always beside the player and these are no-ops.
        let sheetOpen = false;
        const syncPanel = () => { panel.inert = phone.matches && !sheetOpen; };
        function closeSheet(silent) {
          if (!sheetOpen) return syncPanel();
          sheetOpen = false;
          panel.classList.remove('is-open');
          syncPanel();
          if (!silent) $('[data-sk-reel-sheet]', pillSlot)?.focus({ preventScroll: true });
        }
        const openSheet = () => {
          if (!phone.matches) { ($('[data-sk-reel-variant]:checked', panel) || $('[data-sk-reel-add]', panel))?.focus({ preventScroll: true }); return; }
          if (!panelBody.children.length) return;
          sheetOpen = true;
          panel.classList.add('is-open');
          syncPanel();
          $('[data-sk-reel-sheetclose]', panel).focus({ preventScroll: true });
        };
        phone.addEventListener('change', () => { if (!phone.matches) closeSheet(true); syncPanel(); }, opt);

        const open = (i, from) => {
          trigger = from;
          if (!dlg.open) {
            dlg.showModal();
            html.classList.add('sk-reels-lock');
            sync();
          }
          show(i);
          $('[data-sk-reel-close]', dlg)?.focus({ preventScroll: true });
        };
        track.addEventListener('click', (e) => {
          const b = e.target.closest('[data-sk-reel-open]');
          if (!b) return;
          e.preventDefault();
          open(Number(b.dataset.skReelOpen), b);
          if (b.hasAttribute('data-sk-reel-sheet')) openSheet();
        }, opt);

        // Player gestures: tap = play/pause; vertical swipe (or wheel on desktop) = next/previous reel
        let g = null;
        const tap = $('[data-sk-reel-tap]', dlg);
        tap.addEventListener('pointerdown', (e) => { g = { x: e.clientX, y: e.clientY, t: Date.now() }; tap.setPointerCapture?.(e.pointerId); }, opt);
        tap.addEventListener('pointerup', (e) => {
          if (!g) return;
          const dx = e.clientX - g.x, dy = e.clientY - g.y;
          g = null;
          if (Math.abs(dy) > 50 && Math.abs(dy) > Math.abs(dx) && tpls.length > 1) show(cur + (dy < 0 ? 1 : -1), dy < 0 ? 'up' : 'down');
          else if (Math.abs(dx) < 10 && Math.abs(dy) < 10) togglePlay();
        }, opt);
        tap.addEventListener('pointercancel', () => { g = null; }, opt);
        let wheelAt = 0;
        player.addEventListener('wheel', (e) => {
          if (tpls.length < 2 || Math.abs(e.deltaY) < 30 || Date.now() - wheelAt < 700) return;
          wheelAt = Date.now();
          show(cur + (e.deltaY > 0 ? 1 : -1), e.deltaY > 0 ? 'up' : 'down');
        }, { passive: true, ...opt });

        playBtn.addEventListener('click', togglePlay, opt);
        muteBtn.addEventListener('click', () => {
          muted = !muted;
          muteBtn.setAttribute('aria-pressed', String(muted));
          muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
          if (media instanceof HTMLVideoElement) media.muted = muted;
        }, opt);
        seek.addEventListener('input', () => {
          if (!(media instanceof HTMLVideoElement) || !media.duration) return;
          media.currentTime = (Number(seek.value) / 1000) * media.duration;
          fill.style.transform = `scaleX(${Number(seek.value) / 1000})`;
        }, opt);
        qBtn.addEventListener('click', () => {
          const willOpen = qMenu.hidden;
          qMenu.hidden = !willOpen;
          qBtn.setAttribute('aria-expanded', String(willOpen));
          if (willOpen) ($('[aria-checked="true"]', qMenu) || $('button', qMenu))?.focus();
        }, opt);
        qMenu.addEventListener('click', (e) => {
          const b = e.target.closest('[data-q]');
          if (!b || !(media instanceof HTMLVideoElement)) return;
          pref = b.dataset.q;
          try { localStorage.setItem('sk-reel-quality', pref); } catch (_) {}
          $$('[data-q]', qMenu).forEach((x) => x.setAttribute('aria-checked', String(x === b)));
          const at = media.currentTime, wasPaused = media.paused;
          player.classList.add('is-loading');
          stage.classList.add('is-loading');
          media.addEventListener('loadeddata', () => { stage.classList.remove('is-loading'); player.classList.remove('is-loading'); }, { once: true });
          loadVideo(media, tpls[cur].dataset, at);
          if (!wasPaused) playMedia(media);
          closeQ();
          qBtn.focus();
        }, opt);
        qMenu.addEventListener('keydown', (e) => {
          const items = $$('[data-q]', qMenu);
          const k = items.indexOf(document.activeElement);
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); items[(k + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length].focus(); }
        }, opt);

        $('[data-sk-reel-prev]', dlg)?.addEventListener('click', () => show(cur - 1, 'down'), opt);
        $('[data-sk-reel-next]', dlg)?.addEventListener('click', () => show(cur + 1, 'up'), opt);
        $('[data-sk-reel-close]', dlg)?.addEventListener('click', () => dlg.close(), opt);
        $('[data-sk-reel-sheetclose]', dlg)?.addEventListener('click', () => closeSheet(), opt);
        moreBtns.forEach((b) => b.addEventListener('click', () => { const k = Number(b.dataset.skReelGo); if (k !== cur) show(k, k > cur ? 'up' : 'down'); }, opt));
        dlg.addEventListener('click', (e) => {
          if (e.target === dlg || e.target === inner) dlg.close();
          if (!qMenu.hidden && !e.target.closest('[data-sk-reel-quality]')) closeQ();
          if (e.target.closest('[data-sk-reel-sheet]')) openSheet();
        }, opt);
        // Escape closes the innermost layer first: quality menu → sheet → viewer
        dlg.addEventListener('cancel', (e) => {
          if (!qMenu.hidden) { e.preventDefault(); closeQ(); qBtn.focus(); }
          else if (sheetOpen) { e.preventDefault(); closeSheet(); }
        }, opt);
        dlg.addEventListener('keydown', (e) => {
          if (e.target.closest('input, select, textarea, [role=menu]')) return;
          const k = e.key;
          if ((k === 'ArrowDown' || k === 'ArrowRight') && tpls.length > 1) { e.preventDefault(); show(cur + 1, 'up'); }
          else if ((k === 'ArrowUp' || k === 'ArrowLeft') && tpls.length > 1) { e.preventDefault(); show(cur - 1, 'down'); }
          else if ((k === 'k' || (k === ' ' && !e.target.closest('button, a'))) && media instanceof HTMLVideoElement) { e.preventDefault(); togglePlay(); }
          else if (k === 'm') muteBtn.click();
        }, opt);
        dlg.addEventListener('close', () => {
          unload();
          closeSheet(true);
          closeQ();
          hud.replaceChildren();
          pillSlot.replaceChildren();
          panelBody.replaceChildren();
          say('');
          html.classList.remove('sk-reels-lock');
          sync();
          // Back to the card that opened it. A cloned card hands focus to its original, and the track hops by whole
          // sets so the original sits exactly where the clone was.
          let back = trigger;
          const clone = back && back.closest('[data-sk-reel-clone]');
          if (clone) {
            back = $(`[data-sk-reel-item] [data-sk-reel-open="${trigger.dataset.skReelOpen}"]`, track);
            if (back) { track.scrollLeft += back.closest('li').offsetLeft - clone.offsetLeft; pos = wrote = track.scrollLeft; }
          }
          if (back && back.isConnected && !Motion.modalOpen()) back.focus({ preventScroll: true });
        }, opt);

        // Format choice: price, availability, Buy now and the book link follow the selected variant
        dlg.addEventListener('change', (e) => {
          const r = e.target.closest('[data-sk-reel-variant]');
          if (!r) return;
          const box = r.closest('.sk-reelbook');
          const ok = r.dataset.available === 'true';
          $$('[data-sk-reel-vprice]', box).forEach((p) => (p.hidden = p.dataset.skReelVprice !== r.value));
          const add = $('[data-sk-reel-add]', box);
          if (add) { add.disabled = !ok; $('[data-sk-reel-add-label]', add).textContent = ok ? 'Add to bag' : 'Sold out'; }
          const buy = $('[data-sk-reel-buy]', box);
          if (buy) { buy.disabled = !ok; buy.dataset.skBuy = r.value; }
          const view = $('[data-sk-reel-view]', box);
          if (view) { const u = new URL(view.href, location.origin); u.searchParams.set('variant', r.value); view.href = u.pathname + u.search; }
        }, opt);
        const addToBag = async (id, btn) => {
          if (!id || !btn || btn.disabled || btn.classList.contains('is-loading') || btn.classList.contains('is-done')) return;
          say('Adding to your bag…', 'busy');
          const ok = await Cart.add([{ id, quantity: 1 }], btn);
          if (dlg.open) say(ok ? 'Added to your bag' : 'Could not add this book to your bag. Please try again.', ok ? 'ok' : 'error');
        };
        dlg.addEventListener('submit', (e) => {
          const form = e.target.closest('[data-sk-reel-form]');
          if (!form) return;
          e.preventDefault();
          if (media instanceof HTMLVideoElement) media.pause();
          addToBag(Number(new FormData(form).get('id')), e.submitter || $('[data-sk-reel-add]', form));
        }, opt);
        dlg.addEventListener('click', (e) => {
          const q = e.target.closest('[data-sk-reel-quick]');
          if (q) addToBag(Number(q.dataset.skReelQuick), q);
        }, opt);
        // Buy now: the theme's [data-sk-buy] handler (document, bubbling) takes the shopper to checkout / Shiprocket.
        // This capture listener runs first and closes the viewer so that hand-off is never hidden under a modal.
        dlg.addEventListener('click', (e) => {
          const b = e.target.closest('[data-sk-buy]');
          if (!b || b.disabled) return;
          unload();
          dlg.close();
        }, { capture: true, ...opt });
        syncPanel();
      }

      document.addEventListener('shopify:section:unload', (e) => {
        if (!e.target.contains(sec)) return;
        stopPreview();
        if (dlg && dlg.open) { unload(); dlg.close(); }
        html.classList.remove('sk-reels-lock');
        ac.abort();
        cancelAnimationFrame(raf);
        cancelAnimationFrame(scrollFrame);
        clearInterval(tick);
        clearTimeout(idleT);
        Object.values(timers).forEach(clearTimeout);
        io.disconnect();
        ro.disconnect();
      }, opt);

      if (focusMode) sec.classList.add('is-ready');
      build();
      sync();
    });
  }

  /* ---------- Init + Theme Editor support ---------- */
  // Interaction-critical components bind immediately; presentational ones wait for idle time
  // so they don't compete with first render on slow phones.
  const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 200));
  function init(scope) {
    initHeader(scope); initAnnounce(); initProduct(scope); initFacets(scope); initFooter(scope);
    Wishlist.sync(); initWishlistPage(scope);
    initSlideshows(scope); initSpotlight(scope); initReels(scope);
    CartPage.initOffers(scope); CartPage.bind(scope); DeliveryCheck.init(scope); Stamp.init();
    idle(() => { initHero(scope); initRails(scope); initTabs(scope); initRecs(scope); initFbt(scope); });
  }
  // Loader: beginLoading/endLoading, showLoader/hideLoader, navigating() for scripted page loads (sakal-search.js)
  Loader.navigating = navStart;
  window.Sakal = { Cart, Wishlist, Motion, Loader, toast, init, openDialog, closeDialog };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
